import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { formatCurrency } from '../utils/dateFormat';
import { useCurrency } from '../contexts/CurrencyContext';
import { 
  Calendar, FileText, DollarSign, CreditCard, Package, 
  RefreshCw, Plus, Edit, Trash2, MessageSquare, Check, X,
  Clock, AlertTriangle, Send, Eye, UserPlus, QrCode, BarChart3, Mail,
  Upload, Paperclip, File, Download, UserMinus, CheckCheck,
  ShieldCheck, ChevronDown, ChevronUp, CheckCircle2, Users, Bell, Volume2, VolumeX, Languages, Mic, Play, Square, Search
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import JobApplicationQR from './JobApplicationQR';
import JobApplicationAnalytics from './JobApplicationAnalytics';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// File Attachment Component
const FileAttachment = ({ attachments, setAttachments, disabled = false }) => {
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem('token');

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'form_attachment');

      const response = await axios.post(`${API}/files/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setAttachments([...attachments, {
        id: response.data.id,
        filename: response.data.filename,
        size: response.data.size
      }]);
      toast.success('File attached');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  const removeAttachment = (fileId) => {
    setAttachments(attachments.filter(a => a.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="space-y-2">
      <Label>Attachments (Optional)</Label>
      <div className="flex items-center gap-2">
        <label className={`flex-1 flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <input
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            disabled={disabled || uploading}
          />
          {uploading ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 text-slate-400" />
          )}
          <span className="text-sm text-slate-500">
            {uploading ? 'Uploading...' : 'Click to attach file'}
          </span>
        </label>
      </div>
      {attachments.length > 0 && (
        <div className="space-y-1">
          {attachments.map((att) => (
            <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg text-sm">
              <Paperclip className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="flex-1 truncate">{att.filename}</span>
              <span className="text-xs text-slate-400">{formatFileSize(att.size)}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeAttachment(att.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400">
        Allowed: jpg, png, pdf, doc, xlsx, csv (max 10MB)
      </p>
    </div>
  );
};

// View Attachments Component (for viewing submitted forms)
const ViewAttachments = ({ attachmentIds }) => {
  const token = localStorage.getItem('token');
  
  if (!attachmentIds || attachmentIds.length === 0) return null;
  
  return (
    <div className="mt-3 pt-3 border-t">
      <div className="flex items-center gap-2 mb-2">
        <Paperclip className="w-4 h-4 text-slate-400" />
        <span className="text-sm font-medium text-slate-700">Attachments ({attachmentIds.length})</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {attachmentIds.map((fileId, idx) => (
          <a
            key={fileId}
            href={`${API}/files/${fileId}/download?auth=${token}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100"
          >
            <Download className="w-3 h-3" />
            Attachment {idx + 1}
          </a>
        ))}
      </div>
    </div>
  );
};

const Forms = () => {
  const { currencySymbol } = useCurrency();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('');
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [commentText, setCommentText] = useState('');
  
  // Status update dialogs
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusFormData, setStatusFormData] = useState({
    approvedDays: '',
    approvedStartDate: '',
    approvedEndDate: '',
    approvedCommutationDays: '',
    approvedTotalAmount: '',
    rejectionReason: '',
    adminNotes: ''
  });
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const isAdmin = user.role === 'admin';
  const isManager = user.role === 'manager';
  const isStaff = user.role === 'staff';
  const canViewSubmissions = isAdmin || isManager; // Only Admin and Manager can view submissions
  
  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  // Farm Operating Guidelines & Safety Rules
  const [guidelinesOpen, setGuidelinesOpen] = useState(true);
  const [guidelinesContent, setGuidelinesContent] = useState(null);
  const [myGuidelineAck, setMyGuidelineAck] = useState(null); // { acknowledged_at, version }
  const [ackIsCurrent, setAckIsCurrent] = useState(false);
  const [ackLoading, setAckLoading] = useState(false);
  const [ackListOpen, setAckListOpen] = useState(false);
  const [ackList, setAckList] = useState([]);
  const [ackTab, setAckTab] = useState('acknowledged'); // 'acknowledged' | 'pending'
  const [pendingStaff, setPendingStaff] = useState([]);
  const [pendingMeta, setPendingMeta] = useState({ pending_count: 0, total_staff: 0 });
  const [reminding, setReminding] = useState(false);
  // Guidelines editing (admin)
  const [editGuidelinesOpen, setEditGuidelinesOpen] = useState(false);
  const [editIntro, setEditIntro] = useState('');
  const [editRules, setEditRules] = useState([]);
  const [savingGuidelines, setSavingGuidelines] = useState(false);
  const [guideLang, setGuideLang] = useState('en');
  const [speaking, setSpeaking] = useState(false);
  const [listenLang, setListenLang] = useState('en');
  const [fetchingSpeech, setFetchingSpeech] = useState(false);
  const [fetchedTranslations, setFetchedTranslations] = useState({});
  const [loadingText, setLoadingText] = useState(false);
  const [audioModel, setAudioModel] = useState('tts-1');
  const [savingAudioModel, setSavingAudioModel] = useState(false);
  const [fishVoices, setFishVoices] = useState({});
  const [fishVoiceTitles, setFishVoiceTitles] = useState({});
  const [fishEnabled, setFishEnabled] = useState(false);
  const [voiceDialogOpen, setVoiceDialogOpen] = useState(false);
  const [voiceLang, setVoiceLang] = useState('ha');
  const [voiceQuery, setVoiceQuery] = useState('nigerian');
  const [voiceResults, setVoiceResults] = useState([]);
  const [voiceSearching, setVoiceSearching] = useState(false);
  const [savingVoiceId, setSavingVoiceId] = useState('');
  const [previewingId, setPreviewingId] = useState('');
  const previewAudioRef = useRef(null);

  const FARM_GUIDELINES_FALLBACK = [
    "Visitors are not allowed within the farm premises except with the express permission/approval of the Farm Manager and only to areas so designated by the manager.",
    "No employee shall leave the farm premises without permission from the Farm Manager. Any employee who leaves the premises without permission shall be subject to disciplinary actions.",
    "If and when an employee is to depart the farm premises either in observance of a scheduled day(s) off or family emergency, a Day-Off Request Form must be duly completed and approved by the Farm Manager before the employee can be able to leave the farm.",
    "An employee leaving the farm shall consent to the Farm Manager\u2019s request to conduct a search on the employee\u2019s bag/luggage accompanying the employee at the time of departure from the farm. Failure to consent may result in disciplinary action or termination.",
    "Personal belongings (including food items) brought to the farm by an employee returning from home shall be inventoried under the supervision of the Farm Manager.",
    "Theft of any property or product belonging to the farm and/or of co-workers is prohibited. Violation of this policy shall result in immediate termination.",
    "Employees are responsible for the safe handling and protection from damage of all tools, machinery and equipment belonging to the farm. Any misuse or wilful damage to any of the farm properties by an employee shall result in disciplinary action to include but not limited to, financial reimbursement to the farm by the employee through reasonable deduction(s) from the employee salary.",
    "Newly hired employees shall be subject to a three months probationary period.",
    "Employees are entitled to four days paid vacation in a 30-day calendar month. This policy applies only to employees who have completed their probationary period.",
    "Any employee wishing to terminate his/her employment shall give at least thirty days notice to the management. Failure to do so may result in denial of future hiring of the employee by the farm."
  ];
  const guidelineRules = guidelinesContent?.rules || FARM_GUIDELINES_FALLBACK;
  const guidelineIntro = guidelinesContent?.intro || 'The underlisted are the operating guidelines and rules that are binding on all staff at all times whenever they are within the Farm premises.';
  const hasHausa = !!(guidelinesContent?.has_translation);
  const translations = guidelinesContent?.translations || {};
  const availableLangs = guidelinesContent?.available_languages || ['en'];
  const langLabels = { en: 'English', ...(guidelinesContent?.languages || {}) };
  const LISTEN_LANGS = [
    { code: 'en', label: 'English' },
    { code: 'ha', label: 'Hausa' },
    { code: 'yo', label: 'Yoruba' },
    { code: 'ig', label: 'Igbo' },
    { code: 'pcm', label: 'Nigerian Pidgin' },
  ];
  const activeT = (guideLang !== 'en') ? (translations[guideLang] || fetchedTranslations[guideLang]) : null;
  const displayRules = (activeT?.rules?.length) ? activeT.rules : guidelineRules;
  const displayIntro = (activeT?.intro) ? activeT.intro : guidelineIntro;

  // Lightweight renderer: **bold** and lines starting with "- " become sub-bullets
  const renderBold = (str) => {
    const parts = String(str).split(/\*\*(.+?)\*\*/g);
    return parts.map((p, i) => (i % 2 === 1 ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>));
  };
  const renderRichRule = (text) => {
    const lines = String(text || '').split('\n');
    const nodes = [];
    let bullets = [];
    const flush = (key) => {
      if (bullets.length) {
        nodes.push(
          <ul key={`ul-${key}`} className="list-disc pl-5 mt-1 space-y-1 marker:text-emerald-500">
            {bullets.map((b, bi) => <li key={bi}>{renderBold(b)}</li>)}
          </ul>
        );
        bullets = [];
      }
    };
    lines.forEach((ln, i) => {
      const t = ln.trim();
      if (t.startsWith('- ')) {
        bullets.push(t.slice(2));
      } else {
        flush(i);
        if (t) nodes.push(<span key={`t-${i}`} className="block">{renderBold(t)}</span>);
      }
    });
    flush('end');
    return nodes;
  };

  const fetchGuidelines = async () => {
    try {
      const [content, ack, audioCfg] = await Promise.all([
        axios.get(`${API}/forms/farm-guidelines`, getAuthHeader()),
        axios.get(`${API}/forms/farm-guidelines/acknowledgment`, getAuthHeader()),
        axios.get(`${API}/forms/farm-guidelines/audio-settings`, getAuthHeader())
      ]);
      setGuidelinesContent(content.data);
      setMyGuidelineAck(ack.data.acknowledgment);
      setAckIsCurrent(ack.data.is_current);
      setAudioModel(audioCfg.data.tts_model || 'tts-1');
      setFishVoices(audioCfg.data.fish_voices || {});
      setFishVoiceTitles(audioCfg.data.fish_voice_titles || {});
      setFishEnabled(!!audioCfg.data.fish_enabled);
      // Initialize the user's remembered language and load its text if needed
      const pref = content.data.my_preferred_language;
      if (pref && pref !== 'en') {
        setGuideLang(pref);
        const hasText = (content.data.translations || {})[pref];
        if (!hasText) ensureDisplayText(pref);
      }
    } catch (error) {
      // silent
    }
  };

  // Fetch (and cache) the on-screen translated text for a language on demand
  const ensureDisplayText = async (code) => {
    if (code === 'en' || translations[code] || fetchedTranslations[code]) return;
    setLoadingText(true);
    try {
      const res = await axios.get(`${API}/forms/farm-guidelines/speech-text?language=${code}`, getAuthHeader());
      if (res.data.language === code) {
        setFetchedTranslations(prev => ({ ...prev, [code]: { intro: res.data.intro, rules: res.data.rules } }));
      } else {
        toast.message('Translation unavailable — showing English.');
        setGuideLang('en');
      }
    } catch (e) {
      toast.error('Could not load that language.');
      setGuideLang('en');
    } finally {
      setLoadingText(false);
    }
  };

  const handleSelectLanguage = async (code) => {
    setGuideLang(code);
    if (code !== 'en') ensureDisplayText(code);
    try {
      await axios.put(`${API}/me/preferred-language?language=${code}`, {}, getAuthHeader());
    } catch (e) { /* non-blocking */ }
  };

  const handleSetAudioModel = async (model) => {
    setSavingAudioModel(true);
    try {
      await axios.put(`${API}/forms/farm-guidelines/audio-settings`, { tts_model: model }, getAuthHeader());
      setAudioModel(model);
      toast.success(`Audio quality set to ${model === 'tts-1-hd' ? 'HD' : 'Standard'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update audio quality');
    } finally {
      setSavingAudioModel(false);
    }
  };

  const FISH_LANGS = [
    { code: 'ha', label: 'Hausa' },
    { code: 'yo', label: 'Yoruba' },
    { code: 'ig', label: 'Igbo' },
    { code: 'pcm', label: 'Nigerian Pidgin' },
  ];

  const stopPreview = () => {
    if (previewAudioRef.current) { previewAudioRef.current.pause(); previewAudioRef.current = null; }
    setPreviewingId('');
  };

  const searchVoices = async (q = voiceQuery) => {
    setVoiceSearching(true);
    stopPreview();
    try {
      const res = await axios.get(`${API}/forms/farm-guidelines/voice-search?q=${encodeURIComponent(q || '')}&page_size=15`, getAuthHeader());
      setVoiceResults(res.data.voices || []);
    } catch (e) {
      toast.error('Voice search failed');
    } finally {
      setVoiceSearching(false);
    }
  };

  const openVoiceDialog = () => {
    setVoiceDialogOpen(true);
    setVoiceLang('ha');
    setVoiceQuery('nigerian');
    searchVoices('nigerian');
  };

  const previewVoice = (v) => {
    if (previewingId === v.id) { stopPreview(); return; }
    stopPreview();
    if (!v.sample_audio) { toast.message('No preview sample available for this voice'); return; }
    const audio = new Audio(v.sample_audio);
    previewAudioRef.current = audio;
    audio.onended = () => setPreviewingId('');
    audio.onerror = () => { setPreviewingId(''); toast.error('Could not play preview'); };
    setPreviewingId(v.id);
    audio.play().catch(() => { setPreviewingId(''); toast.error('Could not play preview'); });
  };

  const selectVoice = async (v) => {
    setSavingVoiceId(v.id);
    try {
      await axios.put(`${API}/forms/farm-guidelines/voice`, { language: voiceLang, voice_id: v.id, voice_title: v.title }, getAuthHeader());
      setFishVoices(prev => ({ ...prev, [voiceLang]: v.id }));
      setFishVoiceTitles(prev => ({ ...prev, [voiceLang]: v.title }));
      toast.success(`${FISH_LANGS.find(l => l.code === voiceLang)?.label} voice set to "${v.title}". Preparing audio…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set voice');
    } finally {
      setSavingVoiceId('');
    }
  };

  useEffect(() => {
    fetchGuidelines();
    // eslint-disable-next-line
  }, []);

  const openEditGuidelines = () => {
    setEditIntro(guidelineIntro);
    setEditRules([...guidelineRules]);
    setEditGuidelinesOpen(true);
  };

  const handleSaveGuidelines = async () => {
    const cleaned = editRules.map(r => (r || '').trim()).filter(Boolean);
    if (cleaned.length === 0) {
      toast.error('Add at least one guideline rule');
      return;
    }
    setSavingGuidelines(true);
    try {
      const res = await axios.put(`${API}/forms/farm-guidelines`, {
        intro: editIntro.trim(),
        rules: cleaned,
      }, getAuthHeader());
      const langs = res.data.translated_languages || [];
      toast.success(`Guidelines updated${langs.length ? ` & translated to ${langs.join(', ')}` : ''}, pushed to ${res.data.notified} staff`);
      setEditGuidelinesOpen(false);
      await fetchGuidelines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update guidelines');
    } finally {
      setSavingGuidelines(false);
    }
  };

  const handleAcknowledgeGuidelines = async () => {
    setAckLoading(true);
    try {
      const response = await axios.post(`${API}/forms/farm-guidelines/acknowledge`, { language: guideLang }, getAuthHeader());
      setMyGuidelineAck({ acknowledged_at: response.data.acknowledged_at, version: response.data.version, user_name: user.name });
      setAckIsCurrent(true);
      toast.success('Acknowledgment recorded. Thank you.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record acknowledgment');
    } finally {
      setAckLoading(false);
    }
  };

  // Read the guidelines aloud (OpenAI for English, Fish Audio native voices for other languages; cached)
  const audioRef = useRef(null);
  const listenTokenRef = useRef(0);

  const stopSpeaking = () => {
    listenTokenRef.current++;  // cancels any in-flight polling/playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setSpeaking(false);
    setFetchingSpeech(false);
  };

  const playAudioData = (data, token) => {
    if (listenTokenRef.current !== token) return;
    if (data.fallback) toast.message(`${(langLabels[data.language] || data.language)} audio unavailable — reading in English.`);
    const audio = new Audio(`data:${data.mime || 'audio/mpeg'};base64,${data.audio_base64}`);
    audioRef.current = audio;
    audio.onended = () => { setSpeaking(false); audioRef.current = null; };
    audio.onerror = () => { setSpeaking(false); audioRef.current = null; toast.error('Could not play audio.'); };
    setFetchingSpeech(false);
    setSpeaking(true);
    audio.play().catch(() => { setSpeaking(false); toast.error('Tap Listen again to play.'); });
  };

  const handleListen = async (langCode = guideLang) => {
    if (speaking || (audioRef.current && !audioRef.current.paused)) { stopSpeaking(); return; }
    const token = ++listenTokenRef.current;
    setListenLang(langCode);
    setFetchingSpeech(true);
    const url = `${API}/forms/farm-guidelines/speech-audio?language=${langCode}`;
    try {
      const res = await axios.get(url, getAuthHeader());
      if (res.data.status === 'ready') { playAudioData(res.data, token); return; }
      // Generating in the background — poll until ready
      toast.message('Preparing audio… first time for a language can take up to a minute.');
      let attempts = 0;
      const poll = async () => {
        if (listenTokenRef.current !== token) return;
        attempts++;
        try {
          const r = await axios.get(url, getAuthHeader());
          if (listenTokenRef.current !== token) return;
          if (r.data.status === 'ready') { playAudioData(r.data, token); return; }
        } catch (e) { /* keep polling */ }
        if (attempts >= 40) { setFetchingSpeech(false); toast.error('Audio is taking too long. Please try again.'); return; }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setFetchingSpeech(false);
      toast.error('Could not load audio for that language.');
    }
  };

  // Stop playback when language changes or the panel unmounts
  useEffect(() => {
    stopSpeaking();
  }, [guideLang]);
  useEffect(() => () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  }, []);

  const openAckList = async () => {
    setAckListOpen(true);
    setAckTab('acknowledged');
    try {
      const [acks, pending] = await Promise.all([
        axios.get(`${API}/forms/farm-guidelines/acknowledgments`, getAuthHeader()),
        axios.get(`${API}/forms/farm-guidelines/pending`, getAuthHeader())
      ]);
      setAckList(acks.data);
      setPendingStaff(pending.data.pending);
      setPendingMeta({ pending_count: pending.data.pending_count, total_staff: pending.data.total_staff });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load acknowledgments');
    }
  };

  const handleRemind = async (userIds = null) => {
    setReminding(true);
    try {
      const response = await axios.post(`${API}/forms/farm-guidelines/remind`, { user_ids: userIds }, getAuthHeader());
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setReminding(false);
    }
  };

  const handleExportAcks = (format) => {
    const url = `${API}/forms/farm-guidelines/acknowledgments/export?format=${format}`;
    if (format === 'csv') {
      axios.get(url, { ...getAuthHeader(), responseType: 'blob' }).then(res => {
        const blob = new Blob([res.data], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `guideline_acknowledgments_${Date.now()}.csv`;
        link.click();
      }).catch(() => toast.error('Failed to export CSV'));
    } else {
      // Open printable HTML in a new tab (with auth header via fetch -> blob)
      axios.get(url, { ...getAuthHeader(), responseType: 'blob' }).then(res => {
        const blob = new Blob([res.data], { type: 'text/html' });
        const win = window.open(window.URL.createObjectURL(blob), '_blank');
        if (!win) toast.error('Please allow pop-ups to view the PDF');
      }).catch(() => toast.error('Failed to export PDF'));
    }
  };

  const FORM_TYPES = [
    { id: 'day_off_request', label: 'Employee Day-Off Request', icon: Calendar },
    { id: 'incident_report', label: 'Incident Report', icon: AlertTriangle },
    { id: 'loan_request', label: 'Loan Request', icon: DollarSign },
    { id: 'third_party_payment', label: 'Authorization to Pay Salary to Third-Party', icon: CreditCard },
    { id: 'personal_items_inventory', label: 'Personal Items Inventory', icon: Package },
    { id: 'day_off_commutation', label: 'Employee Day-Off Commutation To Cash', icon: RefreshCw },
    { id: 'staff_resignation', label: 'Staff Resignation Form', icon: UserMinus },
    { id: 'job_application', label: 'Job Application', icon: UserPlus },
    { id: 'job_analytics', label: 'Application Analytics', icon: BarChart3, adminOnly: true },
    { id: 'qr_codes', label: 'QR Codes', icon: QrCode, adminOnly: true },
  ];

  const fetchForms = async (formType) => {
    // Only Admin and Manager can view form submissions
    if (!canViewSubmissions) {
      setForms([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/forms?form_type=${formType}`, getAuthHeader());
      setForms(response.data);
    } catch (error) {
      console.error('Failed to fetch forms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeTab) {
      setForms([]);
      return;
    }
    const formType = activeTab.replace(/-/g, '_');
    fetchForms(formType);
    // Clear-on-view: mark this form type as reviewed so its sidebar badge clears
    if (canViewSubmissions && !['job_analytics', 'qr_codes'].includes(formType)) {
      axios.post(`${API}/forms/mark-type-seen/${formType}`, {}, getAuthHeader())
        .then(() => window.dispatchEvent(new Event('formsSeen')))
        .catch(() => {});
    }
  }, [activeTab, canViewSubmissions]);

  // Deep-link: open a specific form type from the sidebar (/forms?type=day-off-request)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get('type');
    if (type) setActiveTab(type);
  }, [location.search]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setDialogOpen(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      under_review: 'bg-blue-100 text-blue-700',
      // Resignation-specific statuses
      accepted: 'bg-emerald-100 text-emerald-700',
      request_meeting: 'bg-amber-100 text-amber-700',
      clearance_pending: 'bg-blue-100 text-blue-700',
      completed: 'bg-purple-100 text-purple-700'
    };
    const labels = {
      pending: 'PENDING',
      approved: 'APPROVED',
      rejected: 'REJECTED',
      under_review: 'UNDER REVIEW',
      accepted: 'ACCEPTED',
      request_meeting: 'MEETING REQUESTED',
      clearance_pending: 'CLEARANCE PENDING',
      completed: 'COMPLETED'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const handleStatusUpdate = async (formId, status) => {
    // For day-off and commutation forms, show dialog for approval modifications or rejection reason
    if (selectedForm && (selectedForm.form_type === 'day_off_request' || selectedForm.form_type === 'day_off_commutation')) {
      if (status === 'approved' || status === 'rejected') {
        setPendingStatus({ formId, status });
        // Pre-fill with original values for approval
        if (status === 'approved') {
          if (selectedForm.form_type === 'day_off_request') {
            setStatusFormData({
              approvedDays: selectedForm.days_requested?.toString() || '',
              approvedStartDate: selectedForm.start_date || '',
              approvedEndDate: selectedForm.end_date || '',
              approvedCommutationDays: '',
              approvedTotalAmount: '',
              rejectionReason: '',
              adminNotes: ''
            });
          } else {
            setStatusFormData({
              approvedDays: '',
              approvedStartDate: '',
              approvedEndDate: '',
              approvedCommutationDays: selectedForm.days_to_convert?.toString() || '',
              approvedTotalAmount: selectedForm.total_amount?.toString() || '',
              rejectionReason: '',
              adminNotes: ''
            });
          }
        } else {
          // Rejection - clear approval fields
          setStatusFormData({
            approvedDays: '',
            approvedStartDate: '',
            approvedEndDate: '',
            approvedCommutationDays: '',
            approvedTotalAmount: '',
            rejectionReason: '',
            adminNotes: ''
          });
        }
        setStatusDialogOpen(true);
        return;
      }
    }
    
    // For other statuses or form types, update directly
    try {
      await axios.put(`${API}/forms/${formId}/status`, { status }, getAuthHeader());
      toast.success(`Form ${status}`);
      fetchForms(activeTab.replace(/-/g, '_'));
      setViewDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleStatusConfirm = async () => {
    if (!pendingStatus) return;
    
    const { formId, status } = pendingStatus;
    
    // Build request payload
    const payload = { status };
    
    if (status === 'approved') {
      if (selectedForm.form_type === 'day_off_request') {
        if (statusFormData.approvedDays) payload.approved_days = parseInt(statusFormData.approvedDays);
        if (statusFormData.approvedStartDate) payload.approved_start_date = statusFormData.approvedStartDate;
        if (statusFormData.approvedEndDate) payload.approved_end_date = statusFormData.approvedEndDate;
      } else if (selectedForm.form_type === 'day_off_commutation') {
        if (statusFormData.approvedCommutationDays) payload.approved_commutation_days = parseInt(statusFormData.approvedCommutationDays);
        if (statusFormData.approvedTotalAmount) payload.approved_total_amount = parseFloat(statusFormData.approvedTotalAmount);
      }
    } else if (status === 'rejected') {
      if (!statusFormData.rejectionReason.trim()) {
        toast.error('Please provide a reason for rejection');
        return;
      }
      payload.rejection_reason = statusFormData.rejectionReason;
    }
    
    if (statusFormData.adminNotes) {
      payload.admin_notes = statusFormData.adminNotes;
    }
    
    try {
      await axios.put(`${API}/forms/${formId}/status`, payload, getAuthHeader());
      toast.success(`Form ${status}. Employee has been notified via direct message.`);
      fetchForms(activeTab.replace(/-/g, '_'));
      setViewDialogOpen(false);
      setStatusDialogOpen(false);
      setPendingStatus(null);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddComment = async (formId) => {
    if (!commentText.trim()) return;
    try {
      await axios.post(`${API}/forms/${formId}/comments`, { comment: commentText }, getAuthHeader());
      toast.success('Comment added');
      setCommentText('');
      // Refresh form details
      const response = await axios.get(`${API}/forms/${formId}`, getAuthHeader());
      setSelectedForm(response.data);
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form?')) return;
    try {
      await axios.delete(`${API}/forms/${formId}`, getAuthHeader());
      toast.success('Form deleted');
      fetchForms(activeTab.replace(/-/g, '_'));
      setViewDialogOpen(false);
    } catch (error) {
      toast.error('Failed to delete form');
    }
  };

  const viewFormDetails = async (form) => {
    try {
      const response = await axios.get(`${API}/forms/${form.id}`, getAuthHeader());
      setSelectedForm(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load form details');
    }
  };

  const selectedFormType = FORM_TYPES.find(t => t.id.replace(/_/g, '-') === activeTab);

  return (
    <div className="space-y-4 md:space-y-6" data-testid="forms-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Employee Forms
          </h1>
          <p className="text-sm md:text-lg text-slate-600 mt-1 md:mt-2">
            {canViewSubmissions 
              ? 'Manage and review employee form submissions' 
              : 'Submit your form requests. Contact Admin or Manager to view or edit submitted forms.'}
          </p>
        </div>
        
        {/* Form Type Dropdown */}
        <div className="w-full sm:w-80">
          <Label className="text-xs text-slate-500 mb-1 block">Select Form Type</Label>
          <Select value={activeTab} onValueChange={handleTabChange}>
            <SelectTrigger className="w-full bg-white">
              <SelectValue placeholder="Select a form type">
                {selectedFormType && (
                  <div className="flex items-center gap-2">
                    <selectedFormType.icon className="w-4 h-4" />
                    <span>{selectedFormType.label}</span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {FORM_TYPES.filter(type => !type.adminOnly || isAdmin).map(type => (
                <SelectItem key={type.id} value={type.id.replace(/_/g, '-')}>
                  <div className="flex items-center gap-2">
                    <type.icon className="w-4 h-4" />
                    <span>{type.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Farm Operating Guidelines & Safety Rules (pinned, all users) */}
      <Collapsible open={guidelinesOpen} onOpenChange={setGuidelinesOpen} className="card border border-emerald-200 bg-emerald-50/40 rounded-xl overflow-hidden" data-testid="farm-guidelines-panel">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between gap-3 px-4 md:px-6 py-4 text-left hover:bg-emerald-50 transition-colors"
            data-testid="farm-guidelines-toggle"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-600 text-white shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base md:text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Farm Operating Guidelines and Safety Rules
                </h2>
                <p className="text-xs md:text-sm text-slate-600">Binding on all staff at all times within the Farm premises</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {guidelinesContent?.is_updated && (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-white bg-amber-500 px-2.5 py-1 rounded-full shadow-sm" data-testid="guidelines-updated-badge">
                  <AlertTriangle className="w-3.5 h-3.5" /> Updated
                </span>
              )}
              {ackIsCurrent && (
                <span className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Acknowledged
                </span>
              )}
              {!ackIsCurrent && myGuidelineAck && (
                <span className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
                  <AlertTriangle className="w-3.5 h-3.5" /> Updated — re-acknowledge
                </span>
              )}
              {guidelinesOpen ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 md:px-6 pb-5 pt-1">
            {/* Language (display) toggle + Listen + HD setting */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="inline-flex items-center gap-1.5 mr-1 text-xs font-semibold text-slate-500">
                <Languages className="w-4 h-4" /> Language:
              </div>
              <div className="inline-flex flex-wrap rounded-lg border border-emerald-200 bg-white p-0.5" data-testid="guidelines-lang-toggle">
                {LISTEN_LANGS.map(({ code, label }) => (
                  <button
                    key={code}
                    onClick={() => handleSelectLanguage(code)}
                    data-testid={`guidelines-lang-${code}`}
                    className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${guideLang === code ? 'bg-emerald-600 text-white' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {loadingText && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700" data-testid="guidelines-text-loading">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Translating…
                </span>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="guidelines-listen-btn"
                    disabled={fetchingSpeech}
                    className={`border-emerald-300 ${speaking ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'text-emerald-700 hover:bg-emerald-50'}`}
                    onClick={(e) => { if (speaking) { e.preventDefault(); stopSpeaking(); } }}
                  >
                    {speaking ? <VolumeX className="w-4 h-4 mr-1.5" /> : <Volume2 className="w-4 h-4 mr-1.5" />}
                    {speaking ? 'Stop' : (fetchingSpeech ? 'Preparing audio…' : 'Listen')}
                    {!speaking && !fetchingSpeech && <ChevronDown className="w-3.5 h-3.5 ml-1.5" />}
                  </Button>
                </DropdownMenuTrigger>
                {!speaking && !fetchingSpeech && (
                  <DropdownMenuContent align="start" className="w-48" data-testid="guidelines-listen-menu">
                    {LISTEN_LANGS.map(({ code, label }) => (
                      <DropdownMenuItem
                        key={code}
                        onSelect={() => handleListen(code)}
                        data-testid={`guidelines-listen-${code}`}
                        className="cursor-pointer"
                      >
                        <Volume2 className="w-4 h-4 mr-2 text-emerald-600" />
                        {label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                )}
              </DropdownMenu>
              {isAdmin && (
                <div className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-white p-0.5 ml-auto" data-testid="guidelines-audio-quality">
                  <span className="text-[11px] font-semibold text-slate-500 px-1.5">Audio:</span>
                  <button
                    type="button"
                    disabled={savingAudioModel}
                    onClick={() => handleSetAudioModel('tts-1')}
                    data-testid="audio-quality-standard"
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${audioModel === 'tts-1' ? 'bg-emerald-600 text-white' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    Standard
                  </button>
                  <button
                    type="button"
                    disabled={savingAudioModel}
                    onClick={() => handleSetAudioModel('tts-1-hd')}
                    data-testid="audio-quality-hd"
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${audioModel === 'tts-1-hd' ? 'bg-emerald-600 text-white' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    HD
                  </button>
                </div>
              )}
              {isAdmin && fishEnabled && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={openVoiceDialog}
                  data-testid="guidelines-voices-btn"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  <Mic className="w-4 h-4 mr-1.5" /> Voices
                </Button>
              )}
            </div>
            <p className="text-sm text-slate-700 mb-4">
              {renderBold(displayIntro)}
            </p>
            {guidelinesContent?.is_updated && guidelinesContent?.updated_at && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 mb-4 inline-flex items-center gap-1" data-testid="guidelines-updated-meta">
                <AlertTriangle className="w-3.5 h-3.5" />
                Updated{guidelinesContent.updated_by ? ` by ${guidelinesContent.updated_by}` : ''} on {formatDate(guidelinesContent.updated_at)}
              </p>
            )}
            <ol className="list-decimal pl-5 space-y-3 text-sm text-slate-700 marker:text-emerald-600 marker:font-semibold">
              {displayRules.map((rule, idx) => (
                <li key={idx} data-testid={`farm-guideline-${idx + 1}`} className="leading-relaxed pl-1">{renderRichRule(rule)}</li>
              ))}
            </ol>

            {/* Acknowledgment */}
            {!ackIsCurrent && myGuidelineAck && (
              <div className="mt-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="guidelines-updated-note">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>These guidelines have been updated since you last acknowledged them. Please review and acknowledge again.</span>
              </div>
            )}
            <div className="mt-5 border-t border-emerald-200 pt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              {ackIsCurrent ? (
                <div className="flex items-center gap-2 text-sm text-emerald-700" data-testid="guidelines-ack-status">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>You acknowledged these guidelines on <strong>{formatDate(myGuidelineAck.acknowledged_at)}</strong></span>
                </div>
              ) : (
                <Button
                  onClick={handleAcknowledgeGuidelines}
                  disabled={ackLoading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="acknowledge-guidelines-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {ackLoading ? 'Saving...' : 'I have read and understood'}
                </Button>
              )}
              {canViewSubmissions && (
                <Button variant="outline" size="sm" onClick={openAckList} data-testid="view-acknowledgments-btn">
                  <Users className="w-4 h-4 mr-2" />
                  View acknowledgments
                </Button>
              )}
              {isAdmin && (
                <Button variant="outline" size="sm" onClick={openEditGuidelines} data-testid="edit-guidelines-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit guidelines
                </Button>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Edit Guidelines dialog (Admin) */}
      <Dialog open={editGuidelinesOpen} onOpenChange={setEditGuidelinesOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-emerald-600" />
              Edit Farm Operating Guidelines
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Saving marks the form as <strong>Updated</strong>, auto-translates it to <strong>Hausa, Yoruba, Igbo & Pidgin</strong>, and pushes it to all staff, who must read and acknowledge it again.</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <Edit className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
              <span>Formatting: wrap text in <code className="px-1 bg-slate-200 rounded">**double asterisks**</code> for <strong>bold</strong>. Start a line with <code className="px-1 bg-slate-200 rounded">- </code> to make an indented sub-point.</span>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Introduction</label>
              <Textarea
                value={editIntro}
                onChange={(e) => setEditIntro(e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="edit-guidelines-intro"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-700">Guideline Rules</label>
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => setEditRules([...editRules, ''])}
                  data-testid="add-guideline-rule-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add rule
                </Button>
              </div>
              <div className="space-y-2">
                {editRules.map((rule, idx) => (
                  <div key={idx} className="flex items-start gap-2" data-testid={`edit-rule-row-${idx}`}>
                    <span className="mt-2 text-xs font-semibold text-emerald-600 w-5 text-right">{idx + 1}.</span>
                    <Textarea
                      value={rule}
                      onChange={(e) => {
                        const next = [...editRules];
                        next[idx] = e.target.value;
                        setEditRules(next);
                      }}
                      rows={2}
                      className="flex-1"
                      data-testid={`edit-guideline-rule-${idx}`}
                    />
                    <Button
                      type="button" variant="ghost" size="icon"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-1"
                      onClick={() => setEditRules(editRules.filter((_, i) => i !== idx))}
                      data-testid={`remove-guideline-rule-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {editRules.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">No rules. Click "Add rule" to create one.</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setEditGuidelinesOpen(false)} data-testid="cancel-edit-guidelines-btn">Cancel</Button>
            <Button
              onClick={handleSaveGuidelines}
              disabled={savingGuidelines}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-guidelines-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {savingGuidelines ? 'Saving & pushing…' : 'Save & push to staff'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Acknowledgments dialog (Admin/Manager): Acknowledged / Pending + reminders + export */}
      <Dialog open={ackListOpen} onOpenChange={setAckListOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Guideline Acknowledgments
            </DialogTitle>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex items-center gap-2 border-b">
            <button
              type="button"
              onClick={() => setAckTab('acknowledged')}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${ackTab === 'acknowledged' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid="ack-tab-acknowledged"
            >
              Acknowledged ({ackList.length})
            </button>
            <button
              type="button"
              onClick={() => setAckTab('pending')}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${ackTab === 'pending' ? 'border-amber-600 text-amber-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid="ack-tab-pending"
            >
              Pending ({pendingMeta.pending_count})
            </button>
          </div>

          {/* Action bar */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExportAcks('csv')} data-testid="export-acks-csv">
                <Download className="w-4 h-4 mr-1.5" /> CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExportAcks('pdf')} data-testid="export-acks-pdf">
                <Download className="w-4 h-4 mr-1.5" /> PDF
              </Button>
            </div>
            {ackTab === 'pending' && pendingStaff.length > 0 && (
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" disabled={reminding} onClick={() => handleRemind(null)} data-testid="remind-all-btn">
                <Bell className="w-4 h-4 mr-1.5" /> {reminding ? 'Sending...' : 'Remind all'}
              </Button>
            )}
          </div>

          <div className="max-h-[50vh] overflow-y-auto" data-testid="ack-list">
            {ackTab === 'acknowledged' ? (
              ackList.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No acknowledgments yet.</p>
              ) : (
                <div className="space-y-2">
                  {/* Language coverage breakdown */}
                  {(() => {
                    const counts = ackList.reduce((acc, a) => {
                      const label = a.language_label || (a.language === 'en' ? 'English' : (a.language ? a.language : 'English'));
                      acc[label] = (acc[label] || 0) + 1;
                      return acc;
                    }, {});
                    const entries = Object.entries(counts).sort((x, y) => y[1] - x[1]);
                    return (
                      <div className="flex flex-wrap items-center gap-1.5 pb-1" data-testid="ack-language-coverage">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 mr-1">
                          <Languages className="w-3.5 h-3.5" /> Read in:
                        </span>
                        {entries.map(([label, count]) => (
                          <span key={label} className="inline-flex items-center gap-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-1" data-testid={`ack-lang-count-${label}`}>
                            {label}: <strong>{count}</strong>
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                  {ackList.map((a, idx) => (
                    <div key={a.id || idx} className="flex items-center justify-between border rounded-lg px-3 py-2 bg-white" data-testid="ack-list-item">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{a.user_name}</p>
                        <p className="text-xs text-slate-400 capitalize">{(a.user_role || '').replace(/_/g, ' ')}</p>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 mb-0.5" data-testid="ack-item-language">
                          <Languages className="w-3 h-3" />
                          {a.language_label || (a.language === 'en' ? 'English' : (a.language || 'English'))}
                        </span>
                        <span className="text-xs text-slate-500 block">{formatDate(a.acknowledged_at)}</span>
                        {!a.is_current && (
                          <span className="text-[10px] text-amber-600 font-medium">outdated version</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              pendingStaff.length === 0 ? (
                <p className="text-center text-emerald-600 py-8">All staff have acknowledged the current guidelines.</p>
              ) : (
                <div className="space-y-2">
                  {pendingStaff.map((p, idx) => (
                    <div key={p.user_id || idx} className="flex items-center justify-between border rounded-lg px-3 py-2 bg-white" data-testid="pending-list-item">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{p.user_name}</p>
                        <p className="text-xs text-slate-400 capitalize">{(p.user_role || '').replace(/_/g, ' ')}</p>
                      </div>
                      <Button variant="ghost" size="sm" className="text-amber-700 hover:text-amber-800 hover:bg-amber-50" disabled={reminding} onClick={() => handleRemind([p.user_id])} data-testid={`remind-${p.user_id}`}>
                        <Bell className="w-4 h-4 mr-1" /> Remind
                      </Button>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Voice Picker dialog (admin) */}
      <Dialog open={voiceDialogOpen} onOpenChange={(o) => { setVoiceDialogOpen(o); if (!o) stopPreview(); }}>
        <DialogContent className="max-w-2xl" data-testid="voice-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-emerald-600" /> Choose Read-Aloud Voices
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Pick the Fish Audio voice that reads each language. Preview a voice, then tap "Use". New audio is prepared automatically.</p>
            {/* Language selector */}
            <div className="inline-flex flex-wrap rounded-lg border border-emerald-200 bg-white p-0.5">
              {FISH_LANGS.map(({ code, label }) => (
                <button
                  key={code}
                  onClick={() => setVoiceLang(code)}
                  data-testid={`voice-lang-${code}`}
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${voiceLang === code ? 'bg-emerald-600 text-white' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="text-xs text-slate-600" data-testid="current-voice-label">
              Current {FISH_LANGS.find(l => l.code === voiceLang)?.label} voice:{' '}
              <span className="font-semibold text-emerald-700">{fishVoiceTitles[voiceLang] || 'Default'}</span>
            </div>
            {/* Search */}
            <div className="flex gap-2">
              <Input
                value={voiceQuery}
                onChange={(e) => setVoiceQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') searchVoices(); }}
                placeholder="Search voices (e.g. nigerian, hausa, deep male)"
                data-testid="voice-search-input"
              />
              <Button onClick={() => searchVoices()} disabled={voiceSearching} data-testid="voice-search-btn" className="bg-emerald-600 hover:bg-emerald-700">
                {voiceSearching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            {/* Results */}
            <div className="max-h-80 overflow-y-auto overflow-x-hidden space-y-2 pr-1" data-testid="voice-results">
              {voiceSearching && voiceResults.length === 0 && (
                <p className="text-center text-slate-400 py-6 text-sm">Searching…</p>
              )}
              {!voiceSearching && voiceResults.length === 0 && (
                <p className="text-center text-slate-400 py-6 text-sm">No voices found. Try another search.</p>
              )}
              {voiceResults.map((v) => {
                const isCurrent = fishVoices[voiceLang] === v.id;
                return (
                  <div key={v.id} className={`grid grid-cols-[auto_1fr_auto] items-center gap-3 border rounded-lg px-3 py-2 ${isCurrent ? 'border-emerald-400 bg-emerald-50' : 'bg-white'}`} data-testid={`voice-item-${v.id}`}>
                    <button
                      onClick={() => previewVoice(v)}
                      disabled={!v.sample_audio}
                      data-testid={`voice-preview-${v.id}`}
                      className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${v.sample_audio ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-slate-200 text-slate-400'}`}
                      title={v.sample_audio ? 'Preview' : 'No sample'}
                    >
                      {previewingId === v.id ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    <div className="min-w-0 overflow-hidden">
                      <p className="text-sm font-medium text-slate-800 truncate">{v.title} {v.languages?.length ? <span className="text-[10px] text-slate-400">({v.languages.join(', ')})</span> : null}</p>
                      {v.description && <p className="text-xs text-slate-400 truncate">{v.description}</p>}
                    </div>
                    <Button
                      size="sm"
                      disabled={isCurrent || savingVoiceId === v.id}
                      onClick={() => selectVoice(v)}
                      data-testid={`voice-use-${v.id}`}
                      className={`shrink-0 ${isCurrent ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                    >
                      {isCurrent ? 'In use' : (savingVoiceId === v.id ? '…' : 'Use')}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Form Content based on selected type */}
      <div className="mt-6">
        {!activeTab && (
          <div className="card p-8 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">Select a Form Type</h3>
            <p className="text-slate-500">Please select a form type from the dropdown above to view or submit forms.</p>
          </div>
        )}

        {activeTab === 'day-off-request' && (
          <DayOffRequestForm 
            forms={forms}
            loading={loading}
            isAdmin={isAdmin}
            canViewSubmissions={canViewSubmissions}
            onRefresh={() => fetchForms('day_off_request')}
            onView={viewFormDetails}
            getStatusBadge={getStatusBadge}
            API={API}
            getAuthHeader={getAuthHeader}
            currentUser={user}
          />
        )}

        {activeTab === 'incident-report' && (
          <IncidentReportForm 
            forms={forms}
            loading={loading}
            isAdmin={isAdmin}
            canViewSubmissions={canViewSubmissions}
            onRefresh={() => fetchForms('incident_report')}
            onView={viewFormDetails}
            getStatusBadge={getStatusBadge}
            API={API}
            getAuthHeader={getAuthHeader}
            currentUser={user}
          />
        )}

        {activeTab === 'loan-request' && (
          <LoanRequestForm 
            forms={forms}
            loading={loading}
            isAdmin={isAdmin}
            canViewSubmissions={canViewSubmissions}
            onRefresh={() => fetchForms('loan_request')}
            onView={viewFormDetails}
            getStatusBadge={getStatusBadge}
            currencySymbol={currencySymbol}
            API={API}
            getAuthHeader={getAuthHeader}
            currentUser={user}
          />
        )}

        {activeTab === 'third-party-payment' && (
          <ThirdPartyPaymentForm 
            forms={forms}
            loading={loading}
            isAdmin={isAdmin}
            canViewSubmissions={canViewSubmissions}
            onRefresh={() => fetchForms('third_party_payment')}
            onView={viewFormDetails}
            getStatusBadge={getStatusBadge}
            currencySymbol={currencySymbol}
            API={API}
            getAuthHeader={getAuthHeader}
            currentUser={user}
          />
        )}

        {activeTab === 'personal-items-inventory' && (
          <PersonalItemsForm 
            forms={forms}
            loading={loading}
            isAdmin={isAdmin}
            canViewSubmissions={canViewSubmissions}
            onRefresh={() => fetchForms('personal_items_inventory')}
            onView={viewFormDetails}
            getStatusBadge={getStatusBadge}
            API={API}
            getAuthHeader={getAuthHeader}
            currentUser={user}
          />
        )}

        {activeTab === 'day-off-commutation' && (
          <DayOffCommutationForm 
            forms={forms}
            loading={loading}
            isAdmin={isAdmin}
            canViewSubmissions={canViewSubmissions}
            onRefresh={() => fetchForms('day_off_commutation')}
            onView={viewFormDetails}
            getStatusBadge={getStatusBadge}
            currencySymbol={currencySymbol}
            API={API}
            getAuthHeader={getAuthHeader}
            currentUser={user}
          />
        )}

        {activeTab === 'staff-resignation' && (
          <StaffResignationForm 
            forms={forms}
            loading={loading}
            isAdmin={isAdmin}
            canViewSubmissions={canViewSubmissions}
            onRefresh={() => fetchForms('staff_resignation')}
            onView={viewFormDetails}
            getStatusBadge={getStatusBadge}
            API={API}
            getAuthHeader={getAuthHeader}
            currentUser={user}
          />
        )}

        {activeTab === 'job-application' && (
          <JobApplicationForm 
            forms={forms}
            loading={loading}
            isAdmin={isAdmin}
            canViewSubmissions={canViewSubmissions}
            onRefresh={() => fetchForms('job_application')}
            onView={viewFormDetails}
            getStatusBadge={getStatusBadge}
            API={API}
            getAuthHeader={getAuthHeader}
            currentUser={user}
          />
        )}

        {activeTab === 'job-analytics' && isAdmin && (
          <JobApplicationAnalytics getAuthHeader={getAuthHeader} />
        )}

        {activeTab === 'qr-codes' && isAdmin && (
          <JobApplicationQR />
        )}
      </div>

      {/* View/Edit Form Dialog - Only for Admin/Manager */}
      {canViewSubmissions && (
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Form Details</DialogTitle>
            </DialogHeader>
            {selectedForm && (
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="font-medium">Employee:</span> {selectedForm.employee_name}</div>
                  <div><span className="font-medium">Status:</span> {getStatusBadge(selectedForm.status)}</div>
                <div><span className="font-medium">Submitted:</span> {formatDate(selectedForm.created_at)}</div>
                {selectedForm.reviewed_by && (
                  <div><span className="font-medium">Reviewed by:</span> {selectedForm.reviewed_by}</div>
                )}
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Form Data</h4>
                {selectedForm.form_type === 'job_application' ? (
                  <div className="space-y-3 text-sm">
                    {/* Personal Info */}
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="font-medium text-slate-700 mb-2">Personal Information</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div><span className="text-slate-500">Name:</span> {selectedForm.first_name} {selectedForm.surname}</div>
                        <div><span className="text-slate-500">Date of Birth:</span> {formatDate(selectedForm.date_of_birth)}</div>
                        <div><span className="text-slate-500">Telephone:</span> {selectedForm.telephone}</div>
                        <div><span className="text-slate-500">Email:</span> {selectedForm.email || 'N/A'}</div>
                        <div className="col-span-2"><span className="text-slate-500">Residence:</span> {selectedForm.residence}</div>
                      </div>
                    </div>
                    
                    {/* Education & Employment */}
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="font-medium text-slate-700 mb-2">Education & Employment</p>
                      <div className="space-y-1">
                        <div><span className="text-slate-500">Education:</span> {selectedForm.education}</div>
                        <div><span className="text-slate-500">Present Job:</span> {selectedForm.present_job || 'N/A'}</div>
                        <div><span className="text-slate-500">Employer:</span> {selectedForm.employer_name || 'N/A'} {selectedForm.employer_location ? `(${selectedForm.employer_location})` : ''}</div>
                      </div>
                    </div>

                    {/* Referees */}
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="font-medium text-slate-700 mb-2">Referees / Guarantors</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="border-r pr-3">
                          <p className="text-xs font-medium text-slate-600 mb-1">Referee 1</p>
                          <div className="text-xs space-y-0.5">
                            <div><span className="text-slate-500">Name:</span> {selectedForm.referee1?.name}</div>
                            <div><span className="text-slate-500">Phone:</span> {selectedForm.referee1?.phone}</div>
                            <div><span className="text-slate-500">Address:</span> {selectedForm.referee1?.address}</div>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-slate-600 mb-1">Referee 2</p>
                          <div className="text-xs space-y-0.5">
                            <div><span className="text-slate-500">Name:</span> {selectedForm.referee2?.name}</div>
                            <div><span className="text-slate-500">Phone:</span> {selectedForm.referee2?.phone}</div>
                            <div><span className="text-slate-500">Address:</span> {selectedForm.referee2?.address}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Driving & Position */}
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="font-medium text-slate-700 mb-2">Driving & Position</p>
                      <div className="space-y-1">
                        <div><span className="text-slate-500">Driving Experience:</span> {selectedForm.driving_experience}</div>
                        <div><span className="text-slate-500">Has Driving License:</span> {selectedForm.has_driving_license ? 'Yes' : 'No'}</div>
                        <div><span className="text-slate-500">Position Applied:</span> <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">{selectedForm.position_applied}</span></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <pre className="bg-slate-50 p-3 rounded text-xs overflow-x-auto">
                    {JSON.stringify(selectedForm, null, 2)}
                  </pre>
                )}
              </div>

              {/* Comments Section */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Comments ({selectedForm.comments?.length || 0})</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedForm.comments?.map((c, i) => (
                    <div key={i} className="bg-slate-50 p-2 rounded text-sm">
                      <div className="font-medium text-xs text-slate-500">{c.user_name} - {formatDate(c.created_at)}</div>
                      <div>{c.comment}</div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input 
                    placeholder="Add a comment..." 
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                  />
                  <Button size="sm" onClick={() => handleAddComment(selectedForm.id)}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Admin Actions */}
              {isAdmin && (
                <div className="border-t pt-4 flex flex-wrap gap-2 pb-2">
                  {selectedForm?.form_type === 'staff_resignation' ? (
                    <>
                      {/* Resignation-specific actions */}
                      <Button 
                        size="sm" 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleStatusUpdate(selectedForm.id, 'accepted')}
                      >
                        <Check className="w-4 h-4 mr-1" /> Accept Resignation
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-amber-600 border-amber-600"
                        onClick={() => handleStatusUpdate(selectedForm.id, 'request_meeting')}
                      >
                        <Calendar className="w-4 h-4 mr-1" /> Request Meeting
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-blue-600 border-blue-600"
                        onClick={() => handleStatusUpdate(selectedForm.id, 'clearance_pending')}
                      >
                        <Clock className="w-4 h-4 mr-1" /> Clearance Pending
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-purple-600 border-purple-600"
                        onClick={() => handleStatusUpdate(selectedForm.id, 'completed')}
                      >
                        <CheckCheck className="w-4 h-4 mr-1" /> Mark Completed
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* Standard form actions */}
                      <Button 
                        size="sm" 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleStatusUpdate(selectedForm.id, 'approved')}
                      >
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-blue-600 border-blue-600"
                        onClick={() => handleStatusUpdate(selectedForm.id, 'under_review')}
                      >
                        <Clock className="w-4 h-4 mr-1" /> Under Review
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 border-red-600"
                        onClick={() => handleStatusUpdate(selectedForm.id, 'rejected')}
                      >
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={() => handleDeleteForm(selectedForm.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}
      
      {/* Status Update Confirmation Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pendingStatus?.status === 'approved' ? 'Approve Request' : 'Reject Request'}
            </DialogTitle>
          </DialogHeader>
          
          {pendingStatus?.status === 'approved' && selectedForm?.form_type === 'day_off_request' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Modify the approval details if needed. The employee will be notified via direct message.
              </p>
              <div>
                <Label>Number of Days Approved</Label>
                <Input
                  type="number"
                  min="1"
                  value={statusFormData.approvedDays}
                  onChange={(e) => setStatusFormData({...statusFormData, approvedDays: e.target.value})}
                  placeholder={selectedForm?.days_requested?.toString()}
                  data-testid="approved-days-input"
                />
              </div>
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={statusFormData.approvedStartDate}
                  onChange={(e) => setStatusFormData({...statusFormData, approvedStartDate: e.target.value})}
                  data-testid="approved-start-date-input"
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={statusFormData.approvedEndDate}
                  onChange={(e) => setStatusFormData({...statusFormData, approvedEndDate: e.target.value})}
                  data-testid="approved-end-date-input"
                />
              </div>
              <div>
                <Label>Admin Notes (Optional)</Label>
                <Textarea
                  value={statusFormData.adminNotes}
                  onChange={(e) => setStatusFormData({...statusFormData, adminNotes: e.target.value})}
                  placeholder="Any additional notes for the employee..."
                  rows={2}
                  data-testid="admin-notes-input"
                />
              </div>
            </div>
          )}
          
          {pendingStatus?.status === 'approved' && selectedForm?.form_type === 'day_off_commutation' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Modify the approval details if needed. The employee will be notified via direct message.
              </p>
              <div>
                <Label>Number of Days Approved for Commutation</Label>
                <Input
                  type="number"
                  min="1"
                  value={statusFormData.approvedCommutationDays}
                  onChange={(e) => {
                    const days = e.target.value;
                    const dailyRate = selectedForm?.daily_rate || 0;
                    setStatusFormData({
                      ...statusFormData, 
                      approvedCommutationDays: days,
                      approvedTotalAmount: (parseFloat(days) * dailyRate).toFixed(2)
                    });
                  }}
                  placeholder={selectedForm?.days_to_convert?.toString()}
                  data-testid="approved-commutation-days-input"
                />
              </div>
              <div>
                <Label>Total Amount to Pay</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={statusFormData.approvedTotalAmount}
                  onChange={(e) => setStatusFormData({...statusFormData, approvedTotalAmount: e.target.value})}
                  placeholder={selectedForm?.total_amount?.toFixed(2)}
                  data-testid="approved-total-amount-input"
                />
                <p className="text-xs text-slate-500 mt-1">Daily Rate: {currencySymbol}{selectedForm?.daily_rate?.toLocaleString()}</p>
              </div>
              <div>
                <Label>Admin Notes (Optional)</Label>
                <Textarea
                  value={statusFormData.adminNotes}
                  onChange={(e) => setStatusFormData({...statusFormData, adminNotes: e.target.value})}
                  placeholder="Any additional notes for the employee..."
                  rows={2}
                  data-testid="admin-notes-commutation-input"
                />
              </div>
            </div>
          )}
          
          {pendingStatus?.status === 'rejected' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Please provide a reason for rejecting this request. The employee will be notified via direct message.
              </p>
              <div>
                <Label className="text-red-600">Reason for Rejection *</Label>
                <Textarea
                  value={statusFormData.rejectionReason}
                  onChange={(e) => setStatusFormData({...statusFormData, rejectionReason: e.target.value})}
                  placeholder="Explain why this request is being rejected..."
                  rows={3}
                  className="border-red-200 focus:border-red-400"
                  data-testid="rejection-reason-input"
                />
              </div>
              <div>
                <Label>Admin Notes (Optional)</Label>
                <Textarea
                  value={statusFormData.adminNotes}
                  onChange={(e) => setStatusFormData({...statusFormData, adminNotes: e.target.value})}
                  placeholder="Any additional notes..."
                  rows={2}
                  data-testid="admin-notes-rejection-input"
                />
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setStatusDialogOpen(false); setPendingStatus(null); }} data-testid="status-dialog-cancel-btn">
              Cancel
            </Button>
            <Button 
              onClick={handleStatusConfirm}
              className={pendingStatus?.status === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="status-dialog-confirm-btn"
            >
              {pendingStatus?.status === 'approved' ? (
                <><Check className="w-4 h-4 mr-1" /> Confirm Approval</>
              ) : (
                <><X className="w-4 h-4 mr-1" /> Confirm Rejection</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== FORM COMPONENTS ====================

// Day-Off Request Form Component
const DayOffRequestForm = ({ forms, loading, isAdmin, canViewSubmissions, onRefresh, onView, getStatusBadge, API, getAuthHeader, currentUser }) => {
  const [open, setOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [formData, setFormData] = useState({
    leave_type: 'annual',
    start_date: '',
    end_date: '',
    days_requested: 1,
    reason: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        attachments: attachments.map(a => a.id)
      };
      await axios.post(`${API}/forms/day-off-request`, submitData, getAuthHeader());
      toast.success('Day-off request submitted');
      setOpen(false);
      setFormData({ leave_type: 'annual', start_date: '', end_date: '', days_requested: 1, reason: '' });
      setAttachments([]);
      onRefresh();
    } catch (error) {
      toast.error('Failed to submit request');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Day-Off Requests</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" /> New Request
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Request Day-Off</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Submitting User Info */}
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="text-sm text-slate-600">Submitting as:</p>
                <p className="font-semibold text-slate-900">{currentUser?.name || 'Unknown User'}</p>
              </div>
              
              <div>
                <Label>Leave Type *</Label>
                <Select value={formData.leave_type} onValueChange={(v) => setFormData({...formData, leave_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">Annual Leave</SelectItem>
                    <SelectItem value="sick">Sick Leave</SelectItem>
                    <SelectItem value="personal">Personal Leave</SelectItem>
                    <SelectItem value="emergency">Emergency Leave</SelectItem>
                    <SelectItem value="earned_days_off">Regular/Scheduled Earned Days-Off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} required />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} required />
                </div>
              </div>
              <div>
                <Label>Days Requested *</Label>
                <Input type="number" min="1" value={formData.days_requested} onChange={(e) => setFormData({...formData, days_requested: parseInt(e.target.value)})} required />
              </div>
              <div>
                <Label>Reason *</Label>
                <Textarea value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} placeholder="Explain the reason for your request" required />
              </div>
              <FileAttachment attachments={attachments} setAttachments={setAttachments} />
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">Submit Request</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Submissions Table - Only for Admin/Manager */}
      {canViewSubmissions ? (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-[600px]">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Leave Type</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Days</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {forms.map(form => (
                  <tr key={form.id}>
                    <td className="font-medium">{form.employee_name}</td>
                    <td className="capitalize">{form.leave_type}</td>
                    <td>{formatDate(form.start_date)}</td>
                    <td>{formatDate(form.end_date)}</td>
                    <td className="text-center">{form.days_requested}</td>
                    <td>{getStatusBadge(form.status)}</td>
                    <td>
                      <Button size="sm" variant="ghost" onClick={() => onView(form)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
          </table>
          {forms.length === 0 && <div className="text-center py-8 text-slate-500">No requests found</div>}
        </div>
      </div>
      ) : (
        <div className="card p-6 text-center">
          <div className="text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">Form submitted successfully will be reviewed by Admin/Manager</p>
            <p className="text-sm mt-1">Contact Admin or Manager to view or edit your submissions</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Incident Report Form Component
const IncidentReportForm = ({ forms, loading, isAdmin, canViewSubmissions, onRefresh, onView, getStatusBadge, API, getAuthHeader, currentUser }) => {
  const [open, setOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [formData, setFormData] = useState({
    incident_date: '',
    incident_time: '',
    location: '',
    description: '',
    severity: 'low',
    witnesses: '',
    immediate_actions: '',
    injuries_reported: false,
    injury_details: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        attachments: attachments.map(a => a.id)
      };
      await axios.post(`${API}/forms/incident-report`, submitData, getAuthHeader());
      toast.success('Incident report submitted');
      setOpen(false);
      setFormData({ incident_date: '', incident_time: '', location: '', description: '', severity: 'low', witnesses: '', immediate_actions: '', injuries_reported: false, injury_details: '' });
      setAttachments([]);
      onRefresh();
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Incident Reports</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-700">
              <Plus className="w-4 h-4 mr-2" /> Report Incident
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Report Incident</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Submitting User Info */}
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="text-sm text-slate-600">Reported by:</p>
                <p className="font-semibold text-slate-900">{currentUser?.name || 'Unknown User'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={formData.incident_date} onChange={(e) => setFormData({...formData, incident_date: e.target.value})} required />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Input type="time" value={formData.incident_time} onChange={(e) => setFormData({...formData, incident_time: e.target.value})} required />
                </div>
              </div>
              <div>
                <Label>Location *</Label>
                <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} placeholder="Where did it happen?" required />
              </div>
              <div>
                <Label>Severity *</Label>
                <Select value={formData.severity} onValueChange={(v) => setFormData({...formData, severity: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Describe what happened" required />
              </div>
              <div>
                <Label>Witnesses</Label>
                <Input value={formData.witnesses} onChange={(e) => setFormData({...formData, witnesses: e.target.value})} placeholder="Names of any witnesses" />
              </div>
              <div>
                <Label>Immediate Actions Taken</Label>
                <Textarea value={formData.immediate_actions} onChange={(e) => setFormData({...formData, immediate_actions: e.target.value})} placeholder="What was done immediately?" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="injuries" checked={formData.injuries_reported} onChange={(e) => setFormData({...formData, injuries_reported: e.target.checked})} />
                <Label htmlFor="injuries">Injuries Reported</Label>
              </div>
              {formData.injuries_reported && (
                <div>
                  <Label>Injury Details</Label>
                  <Textarea value={formData.injury_details} onChange={(e) => setFormData({...formData, injury_details: e.target.value})} placeholder="Describe injuries" />
                </div>
              )}
              <FileAttachment attachments={attachments} setAttachments={setAttachments} />
              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700">Submit Report</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Submissions Table - Only for Admin/Manager */}
      {canViewSubmissions ? (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-[700px]">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date/Time</th>
                  <th>Location</th>
                  <th>Severity</th>
                  <th>Injuries</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {forms.map(form => (
                  <tr key={form.id}>
                    <td className="font-medium">{form.employee_name}</td>
                    <td>{formatDate(form.incident_date)} {form.incident_time}</td>
                    <td>{form.location}</td>
                    <td>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        form.severity === 'critical' ? 'bg-red-100 text-red-700' :
                        form.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                        form.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {form.severity?.toUpperCase()}
                      </span>
                    </td>
                    <td>{form.injuries_reported ? 'Yes' : 'No'}</td>
                    <td>{getStatusBadge(form.status)}</td>
                    <td>
                      <Button size="sm" variant="ghost" onClick={() => onView(form)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {forms.length === 0 && <div className="text-center py-8 text-slate-500">No reports found</div>}
          </div>
        </div>
      ) : (
        <div className="card p-6 text-center">
          <div className="text-slate-500">
            <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">Form submitted successfully will be reviewed by Admin/Manager</p>
            <p className="text-sm mt-1">Contact Admin or Manager to view or edit your submissions</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Loan Request Form Component
const LoanRequestForm = ({ forms, loading, isAdmin, canViewSubmissions, onRefresh, onView, getStatusBadge, currencySymbol, API, getAuthHeader, currentUser }) => {
  const [open, setOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [formData, setFormData] = useState({
    amount_requested: '',
    purpose: '',
    repayment_months: 6,
    monthly_deduction: ''
  });

  const calculateMonthlyDeduction = () => {
    if (formData.amount_requested && formData.repayment_months) {
      const deduction = parseFloat(formData.amount_requested) / formData.repayment_months;
      setFormData({...formData, monthly_deduction: deduction.toFixed(2)});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/forms/loan-request`, {
        ...formData,
        amount_requested: parseFloat(formData.amount_requested),
        monthly_deduction: parseFloat(formData.monthly_deduction),
        attachments: attachments.map(a => a.id)
      }, getAuthHeader());
      toast.success('Loan request submitted');
      setOpen(false);
      setFormData({ amount_requested: '', purpose: '', repayment_months: 6, monthly_deduction: '' });
      setAttachments([]);
      onRefresh();
    } catch (error) {
      toast.error('Failed to submit request');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Loan Requests</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" /> Request Loan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Request Loan</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Submitting User Info */}
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="text-sm text-slate-600">Requesting as:</p>
                <p className="font-semibold text-slate-900">{currentUser?.name || 'Unknown User'}</p>
              </div>
              
              <div>
                <Label>Amount Requested ({currencySymbol}) *</Label>
                <Input 
                  type="number" 
                  min="0" 
                  step="0.01"
                  value={formData.amount_requested} 
                  onChange={(e) => setFormData({...formData, amount_requested: e.target.value})} 
                  onBlur={calculateMonthlyDeduction}
                  required 
                />
              </div>
              <div>
                <Label>Purpose *</Label>
                <Textarea value={formData.purpose} onChange={(e) => setFormData({...formData, purpose: e.target.value})} placeholder="Why do you need this loan?" required />
              </div>
              <div>
                <Label>Repayment Period (Months) *</Label>
                <Select value={formData.repayment_months.toString()} onValueChange={(v) => {
                  setFormData({...formData, repayment_months: parseInt(v)});
                  setTimeout(calculateMonthlyDeduction, 100);
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[3, 6, 9, 12, 18, 24].map(m => (
                      <SelectItem key={m} value={m.toString()}>{m} months</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Monthly Deduction ({currencySymbol}) *</Label>
                <Input 
                  type="number" 
                  min="0" 
                  step="0.01"
                  value={formData.monthly_deduction} 
                  onChange={(e) => setFormData({...formData, monthly_deduction: e.target.value})} 
                  required 
                />
              </div>
              <FileAttachment attachments={attachments} setAttachments={setAttachments} />
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">Submit Request</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Submissions Table - Only for Admin/Manager */}
      {canViewSubmissions ? (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-[600px]">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date</th>
                  <th className="text-right">Amount</th>
                  <th>Repayment</th>
                  <th className="text-right">Monthly</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {forms.map(form => (
                  <tr key={form.id}>
                    <td className="font-medium">{form.employee_name}</td>
                    <td>{formatDate(form.request_date)}</td>
                    <td className="text-right font-semibold">{currencySymbol}{formatCurrency(form.amount_requested)}</td>
                    <td>{form.repayment_months} months</td>
                    <td className="text-right">{currencySymbol}{formatCurrency(form.monthly_deduction)}</td>
                    <td>{getStatusBadge(form.status)}</td>
                    <td>
                      <Button size="sm" variant="ghost" onClick={() => onView(form)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {forms.length === 0 && <div className="text-center py-8 text-slate-500">No requests found</div>}
          </div>
        </div>
      ) : (
        <div className="card p-6 text-center">
          <div className="text-slate-500">
            <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">Form submitted successfully will be reviewed by Admin/Manager</p>
            <p className="text-sm mt-1">Contact Admin or Manager to view or edit your submissions</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Third-Party Payment Form Component
const ThirdPartyPaymentForm = ({ forms, loading, isAdmin, canViewSubmissions, onRefresh, onView, getStatusBadge, currencySymbol, API, getAuthHeader, currentUser }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    beneficiary_name: '',
    relationship: '',
    bank_name: '',
    account_number: '',
    amount: '',
    duration_months: 1,
    reason: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/forms/third-party-payment`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, getAuthHeader());
      toast.success('Payment request submitted');
      setOpen(false);
      setFormData({ beneficiary_name: '', relationship: '', bank_name: '', account_number: '', amount: '', duration_months: 1, reason: '' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to submit request');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Salary Payment to Third-Party</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" /> New Request
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Request Third-Party Payment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Submitting User Info */}
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="text-sm text-slate-600">Requesting as:</p>
                <p className="font-semibold text-slate-900">{currentUser?.name || 'Unknown User'}</p>
              </div>
              
              <div>
                <Label>Beneficiary Name *</Label>
                <Input value={formData.beneficiary_name} onChange={(e) => setFormData({...formData, beneficiary_name: e.target.value})} placeholder="Full name of recipient" required />
              </div>
              <div>
                <Label>Relationship *</Label>
                <Select value={formData.relationship} onValueChange={(v) => setFormData({...formData, relationship: v})}>
                  <SelectTrigger><SelectValue placeholder="Select relationship" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="spouse">Spouse</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="child">Child</SelectItem>
                    <SelectItem value="sibling">Sibling</SelectItem>
                    <SelectItem value="creditor">Creditor</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bank Name *</Label>
                <Input value={formData.bank_name} onChange={(e) => setFormData({...formData, bank_name: e.target.value})} placeholder="Bank name" required />
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input value={formData.account_number} onChange={(e) => setFormData({...formData, account_number: e.target.value})} placeholder="Account number" required />
              </div>
              <div>
                <Label>Amount ({currencySymbol}) *</Label>
                <Input type="number" min="0" step="0.01" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} required />
              </div>
              <div>
                <Label>Duration (Months) *</Label>
                <Input type="number" min="1" value={formData.duration_months} onChange={(e) => setFormData({...formData, duration_months: parseInt(e.target.value)})} required />
              </div>
              <div>
                <Label>Reason</Label>
                <Textarea value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} placeholder="Why is this payment needed?" />
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">Submit Request</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Submissions Table - Only for Admin/Manager */}
      {canViewSubmissions ? (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-[700px]">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Beneficiary</th>
                  <th>Relationship</th>
                  <th>Bank</th>
                  <th className="text-right">Amount</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {forms.map(form => (
                  <tr key={form.id}>
                    <td className="font-medium">{form.employee_name}</td>
                    <td>{form.beneficiary_name}</td>
                    <td className="capitalize">{form.relationship}</td>
                    <td>{form.bank_name}</td>
                    <td className="text-right font-semibold">{currencySymbol}{formatCurrency(form.amount)}</td>
                    <td>{form.duration_months} months</td>
                    <td>{getStatusBadge(form.status)}</td>
                    <td>
                      <Button size="sm" variant="ghost" onClick={() => onView(form)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {forms.length === 0 && <div className="text-center py-8 text-slate-500">No requests found</div>}
          </div>
        </div>
      ) : (
        <div className="card p-6 text-center">
          <div className="text-slate-500">
            <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">Form submitted successfully will be reviewed by Admin/Manager</p>
            <p className="text-sm mt-1">Contact Admin or Manager to view or edit your submissions</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Personal Items Inventory Form Component
const PersonalItemsForm = ({ forms, loading, isAdmin, canViewSubmissions, onRefresh, onView, getStatusBadge, API, getAuthHeader, currentUser }) => {
  const [open, setOpen] = useState(false);
  const [inventoryReason, setInventoryReason] = useState('');
  const [selectedPerson, setSelectedPerson] = useState('');
  const [customPersonName, setCustomPersonName] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [items, setItems] = useState([{ item_name: '', description: '', serial_number: '', condition: 'good', date_acquired: '' }]);

  const INVENTORY_REASONS = [
    { value: 'new_staff_resuming', label: 'New Staff Resuming' },
    { value: 'staff_disengagement', label: 'Staff Disengagement' },
    { value: 'staff_going_for_day_off', label: 'Staff Going Out for Day-Off' },
    { value: 'staff_resuming_from_day_off', label: 'Staff Resuming from Day-Off' },
    { value: 'random_inventory', label: 'Random Inventory' },
    { value: 'visitor_entry', label: 'Visitor/Non-Employee Entry' },
    { value: 'visitor_exit', label: 'Visitor/Non-Employee Exit' },
  ];

  // Fetch staff list when dialog opens
  useEffect(() => {
    if (open) {
      fetchStaffList();
    }
  }, [open]);

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/staff-for-payroll`, getAuthHeader());
      // Filter out specific names that should not appear in the dropdown
      const excludedNames = ['Niyi Aina', 'Rashidat Aina', 'Waliu Aina', 'Semiu Okewale'];
      const filteredList = (response.data || []).filter(staff => {
        const staffName = staff.user?.name || staff.name || '';
        return !excludedNames.includes(staffName);
      });
      setStaffList(filteredList);
    } catch (error) {
      console.error('Failed to fetch staff list');
    }
  };

  const addItem = () => {
    setItems([...items, { item_name: '', description: '', serial_number: '', condition: 'good', date_acquired: '' }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inventoryReason) {
      toast.error('Please select a reason for inventory');
      return;
    }
    if (!selectedPerson) {
      toast.error('Please select a person');
      return;
    }
    if (selectedPerson === 'other' && !customPersonName.trim()) {
      toast.error('Please enter the person\'s name');
      return;
    }
    
    // Determine the person name to submit
    const personName = selectedPerson === 'other' ? customPersonName.trim() : selectedPerson;
    
    try {
      await axios.post(`${API}/forms/personal-items-inventory`, { 
        inventory_reason: inventoryReason,
        person_name: personName,
        is_non_employee: selectedPerson === 'other',
        items 
      }, getAuthHeader());
      toast.success('Inventory submitted');
      setOpen(false);
      setInventoryReason('');
      setSelectedPerson('');
      setCustomPersonName('');
      setItems([{ item_name: '', description: '', serial_number: '', condition: 'good', date_acquired: '' }]);
      onRefresh();
    } catch (error) {
      toast.error('Failed to submit inventory');
    }
  };

  const getReasonLabel = (value) => {
    return INVENTORY_REASONS.find(r => r.value === value)?.label || value;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Personal Items Inventory</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" /> Submit Inventory
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Personal Items Inventory</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Submitting User Info */}
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="text-sm text-slate-600">Inventory submitted by:</p>
                <p className="font-semibold text-slate-900">{currentUser?.name || 'Unknown User'}</p>
              </div>
              
              <div>
                <Label>Reason for Inventory *</Label>
                <Select value={inventoryReason} onValueChange={setInventoryReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select reason for inventory" />
                  </SelectTrigger>
                  <SelectContent>
                    {INVENTORY_REASONS.map(reason => (
                      <SelectItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Person Name *</Label>
                <Select value={selectedPerson} onValueChange={(v) => { setSelectedPerson(v); if (v !== 'other') setCustomPersonName(''); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select person or choose Other" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffList.map(staff => (
                      <SelectItem key={staff.user?.id || staff.id} value={staff.user?.name || staff.name}>
                        {staff.user?.name || staff.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="other" className="border-t mt-1 pt-1 text-amber-700 font-medium">
                      Other (Non-Employee)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {selectedPerson === 'other' && (
                <div>
                  <Label>Enter Person's Name *</Label>
                  <Input 
                    value={customPersonName} 
                    onChange={(e) => setCustomPersonName(e.target.value)}
                    placeholder="Enter name of visitor/non-employee"
                    required
                  />
                </div>
              )}
              
              {items.map((item, index) => (
                <div key={index} className="border p-3 rounded space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Item {index + 1}</span>
                    {items.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Item Name *</Label>
                      <Input value={item.item_name} onChange={(e) => updateItem(index, 'item_name', e.target.value)} required />
                    </div>
                    <div>
                      <Label>Condition *</Label>
                      <Select value={item.condition} onValueChange={(v) => updateItem(index, 'condition', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="good">Good</SelectItem>
                          <SelectItem value="fair">Fair</SelectItem>
                          <SelectItem value="poor">Poor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Serial Number</Label>
                      <Input value={item.serial_number} onChange={(e) => updateItem(index, 'serial_number', e.target.value)} />
                    </div>
                    <div>
                      <Label>Date Acquired</Label>
                      <Input type="date" value={item.date_acquired} onChange={(e) => updateItem(index, 'date_acquired', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} placeholder="Brief description" />
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addItem} className="w-full">
                <Plus className="w-4 h-4 mr-2" /> Add Another Item
              </Button>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">Submit Inventory</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Submissions Table - Only for Admin/Manager */}
      {canViewSubmissions ? (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-[700px]">
              <thead>
                <tr>
                  <th>Submitted By</th>
                  <th>Person Inventoried</th>
                  <th>Submitted</th>
                  <th>Reason</th>
                  <th>Items</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {forms.map(form => (
                  <tr key={form.id}>
                    <td className="font-medium">{form.employee_name}</td>
                    <td>
                      <span className={form.is_non_employee ? 'text-amber-600' : ''}>
                        {form.person_name || form.employee_name}
                        {form.is_non_employee && <span className="text-xs ml-1">(Visitor)</span>}
                      </span>
                    </td>
                    <td>{formatDate(form.submission_date)}</td>
                    <td>{getReasonLabel(form.inventory_reason)}</td>
                    <td className="text-center">{form.items?.length || 0}</td>
                    <td>{getStatusBadge(form.status)}</td>
                    <td>
                      <Button size="sm" variant="ghost" onClick={() => onView(form)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {forms.length === 0 && <div className="text-center py-8 text-slate-500">No inventories found</div>}
          </div>
        </div>
      ) : (
        <div className="card p-6 text-center">
          <div className="text-slate-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">Form submitted successfully will be reviewed by Admin/Manager</p>
            <p className="text-sm mt-1">Contact Admin or Manager to view or edit your submissions</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Day-Off Commutation Form Component
const DayOffCommutationForm = ({ forms, loading, isAdmin, canViewSubmissions, onRefresh, onView, getStatusBadge, currencySymbol, API, getAuthHeader, currentUser }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    available_days: '',
    days_to_convert: '',
    daily_rate: '',
    total_amount: 0,
    reason: ''
  });

  const calculateTotal = () => {
    if (formData.days_to_convert && formData.daily_rate) {
      const total = parseFloat(formData.days_to_convert) * parseFloat(formData.daily_rate);
      setFormData({...formData, total_amount: total});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/forms/day-off-commutation`, {
        ...formData,
        available_days: parseInt(formData.available_days),
        days_to_convert: parseInt(formData.days_to_convert),
        daily_rate: parseFloat(formData.daily_rate),
        total_amount: parseFloat(formData.total_amount)
      }, getAuthHeader());
      toast.success('Commutation request submitted');
      setOpen(false);
      setFormData({ available_days: '', days_to_convert: '', daily_rate: '', total_amount: 0, reason: '' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to submit request');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Day-Off Commutation to Cash</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" /> New Request
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Convert Day-Off to Cash</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Submitting User Info */}
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="text-sm text-slate-600">Requesting as:</p>
                <p className="font-semibold text-slate-900">{currentUser?.name || 'Unknown User'}</p>
              </div>
              
              <div>
                <Label>Available Day-Offs *</Label>
                <Input type="number" min="0" value={formData.available_days} onChange={(e) => setFormData({...formData, available_days: e.target.value})} required />
              </div>
              <div>
                <Label>Days to Convert *</Label>
                <Input 
                  type="number" 
                  min="1" 
                  max={formData.available_days || 999}
                  value={formData.days_to_convert} 
                  onChange={(e) => setFormData({...formData, days_to_convert: e.target.value})} 
                  onBlur={calculateTotal}
                  required 
                />
              </div>
              <div>
                <Label>Daily Rate ({currencySymbol}) *</Label>
                <Input 
                  type="number" 
                  min="0" 
                  step="0.01"
                  value={formData.daily_rate} 
                  onChange={(e) => setFormData({...formData, daily_rate: e.target.value})} 
                  onBlur={calculateTotal}
                  required 
                />
              </div>
              <div className="bg-emerald-50 p-3 rounded">
                <Label>Total Amount</Label>
                <p className="text-2xl font-bold text-emerald-600">{currencySymbol}{formatCurrency(formData.total_amount)}</p>
              </div>
              <div>
                <Label>Reason</Label>
                <Textarea value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} placeholder="Optional reason for conversion" />
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">Submit Request</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-[600px]">
            <thead>
              <tr>
                {isAdmin && <th>Employee</th>}
                <th>Date</th>
                <th className="text-center">Available</th>
                <th className="text-center">Converting</th>
                <th className="text-right">Daily Rate</th>
                <th className="text-right">Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {forms.map(form => (
                <tr key={form.id}>
                  {isAdmin && <td className="font-medium">{form.employee_name}</td>}
                  <td>{formatDate(form.request_date)}</td>
                  <td className="text-center">{form.available_days} days</td>
                  <td className="text-center">{form.days_to_convert} days</td>
                  <td className="text-right">{currencySymbol}{formatCurrency(form.daily_rate)}</td>
                  <td className="text-right font-semibold">{currencySymbol}{formatCurrency(form.total_amount)}</td>
                  <td>{getStatusBadge(form.status)}</td>
                  <td>
                    <Button size="sm" variant="ghost" onClick={() => onView(form)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {forms.length === 0 && <div className="text-center py-8 text-slate-500">No requests found</div>}
        </div>
      </div>
    </div>
  );
};

// Job Application Form Component
const JobApplicationForm = ({ forms, loading, isAdmin, canViewSubmissions, onRefresh, onView, getStatusBadge, API, getAuthHeader, currentUser }) => {
  const [open, setOpen] = useState(false);
  const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);
  const [resubmissionDialogOpen, setResubmissionDialogOpen] = useState(false);
  const [resubmissionEmail, setResubmissionEmail] = useState('');
  const [sendingResubmission, setSendingResubmission] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [interviewData, setInterviewData] = useState({
    interview_date: '',
    interview_time: '',
    interview_location: '',
    interview_notes: ''
  });
  const [formData, setFormData] = useState({
    surname: '',
    first_name: '',
    date_of_birth: '',
    residence: '',
    telephone: '',
    email: '',
    education: '',
    present_job: '',
    employer_name: '',
    employer_location: '',
    referee1_name: '',
    referee1_phone: '',
    referee1_address: '',
    referee2_name: '',
    referee2_phone: '',
    referee2_address: '',
    driving_experience: '',
    has_driving_license: false,
    position_applied: ''
  });

  const POSITIONS = [
    'Farm Associate',
    'Sales Associate',
    'Snail House Associate',
    'Driver',
    'Distributor'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.surname || !formData.first_name || !formData.date_of_birth || 
        !formData.residence || !formData.telephone || !formData.education ||
        !formData.referee1_name || !formData.referee1_phone || !formData.referee1_address ||
        !formData.referee2_name || !formData.referee2_phone || !formData.referee2_address ||
        !formData.position_applied) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const submitData = {
        surname: formData.surname,
        first_name: formData.first_name,
        date_of_birth: formData.date_of_birth,
        residence: formData.residence,
        telephone: formData.telephone,
        email: formData.email || null,
        education: formData.education,
        present_job: formData.present_job || null,
        employer_name: formData.employer_name || null,
        employer_location: formData.employer_location || null,
        referee1: {
          name: formData.referee1_name,
          phone: formData.referee1_phone,
          address: formData.referee1_address
        },
        referee2: {
          name: formData.referee2_name,
          phone: formData.referee2_phone,
          address: formData.referee2_address
        },
        driving_experience: formData.driving_experience,
        has_driving_license: formData.has_driving_license,
        position_applied: formData.position_applied
      };

      await axios.post(`${API}/forms/job-application`, submitData);
      toast.success('Job application submitted successfully');
      setOpen(false);
      setFormData({
        surname: '',
        first_name: '',
        date_of_birth: '',
        residence: '',
        telephone: '',
        email: '',
        education: '',
        present_job: '',
        employer_name: '',
        employer_location: '',
        referee1_name: '',
        referee1_phone: '',
        referee1_address: '',
        referee2_name: '',
        referee2_phone: '',
        referee2_address: '',
        driving_experience: '',
        has_driving_license: false,
        position_applied: ''
      });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
  };

  const handleScheduleInterview = async () => {
    if (!interviewData.interview_date || !interviewData.interview_time || !interviewData.interview_location) {
      toast.error('Please fill in date, time, and location');
      return;
    }

    try {
      await axios.put(
        `${API}/forms/job-application/${selectedApplication.id}/interview`,
        interviewData,
        getAuthHeader()
      );
      toast.success('Interview scheduled successfully! Email notification sent to applicant.');
      setInterviewDialogOpen(false);
      setSelectedApplication(null);
      setInterviewData({ interview_date: '', interview_time: '', interview_location: '', interview_notes: '' });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule interview');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/forms/job-applications/export`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `job_applications_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Applications exported successfully!');
    } catch (error) {
      toast.error('Failed to export applications');
    }
  };

  const openInterviewDialog = (app) => {
    setSelectedApplication(app);
    setInterviewData({
      interview_date: app.interview_date || '',
      interview_time: app.interview_time || '',
      interview_location: app.interview_location || '',
      interview_notes: app.interview_notes || ''
    });
    setInterviewDialogOpen(true);
  };

  const openResubmissionDialog = (app) => {
    setSelectedApplication(app);
    setResubmissionEmail(app.email || '');
    setResubmissionDialogOpen(true);
  };

  const handleSendResubmissionLink = async () => {
    if (!resubmissionEmail) {
      toast.error('Email address is required');
      return;
    }

    setSendingResubmission(true);
    try {
      const response = await axios.post(
        `${API}/forms/job-applications/resubmission-token?email=${encodeURIComponent(resubmissionEmail)}`,
        {},
        getAuthHeader()
      );
      toast.success('Resubmission link sent to applicant!');
      setResubmissionDialogOpen(false);
      setResubmissionEmail('');
      setSelectedApplication(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send resubmission link');
    } finally {
      setSendingResubmission(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Job Applications</h3>
        <div className="flex gap-2">
          {canViewSubmissions && forms.length > 0 && (
            <Button variant="outline" onClick={handleExportCSV}>
              <FileText className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" /> New Application
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Job Application Form</DialogTitle>
              <p className="text-sm text-slate-500">Apply for a position at Wilyrash Farm</p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Personal Information */}
              <div className="border-b pb-3">
                <h4 className="font-medium text-slate-800 mb-3">Personal Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Surname (Last Name) *</Label>
                    <Input 
                      value={formData.surname} 
                      onChange={(e) => setFormData({...formData, surname: e.target.value})} 
                      placeholder="Enter surname"
                      required 
                    />
                  </div>
                  <div>
                    <Label>First Name *</Label>
                    <Input 
                      value={formData.first_name} 
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})} 
                      placeholder="Enter first name"
                      required 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label>Date of Birth *</Label>
                    <Input 
                      type="date" 
                      value={formData.date_of_birth} 
                      onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} 
                      required 
                    />
                  </div>
                  <div>
                    <Label>Telephone Number *</Label>
                    <Input 
                      value={formData.telephone} 
                      onChange={(e) => setFormData({...formData, telephone: e.target.value})} 
                      placeholder="Enter phone number"
                      required 
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Residence (Address) *</Label>
                  <Textarea 
                    value={formData.residence} 
                    onChange={(e) => setFormData({...formData, residence: e.target.value})} 
                    placeholder="Enter your residential address"
                    required 
                  />
                </div>
                <div className="mt-3">
                  <Label>Email Address (Optional)</Label>
                  <Input 
                    type="email"
                    value={formData.email} 
                    onChange={(e) => setFormData({...formData, email: e.target.value})} 
                    placeholder="Enter email address (if any)"
                  />
                </div>
              </div>

              {/* Education & Employment */}
              <div className="border-b pb-3">
                <h4 className="font-medium text-slate-800 mb-3">Education & Employment</h4>
                <div>
                  <Label>Education *</Label>
                  <Textarea 
                    value={formData.education} 
                    onChange={(e) => setFormData({...formData, education: e.target.value})} 
                    placeholder="Enter your educational background (schools attended, qualifications, etc.)"
                    required 
                  />
                </div>
                <div className="mt-3">
                  <Label>Present Job (if employed)</Label>
                  <Input 
                    value={formData.present_job} 
                    onChange={(e) => setFormData({...formData, present_job: e.target.value})} 
                    placeholder="Enter your current job title"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label>Employer Name</Label>
                    <Input 
                      value={formData.employer_name} 
                      onChange={(e) => setFormData({...formData, employer_name: e.target.value})} 
                      placeholder="Name of current employer"
                    />
                  </div>
                  <div>
                    <Label>Employer Location</Label>
                    <Input 
                      value={formData.employer_location} 
                      onChange={(e) => setFormData({...formData, employer_location: e.target.value})} 
                      placeholder="Location of employer"
                    />
                  </div>
                </div>
              </div>

              {/* Referees/Guarantors */}
              <div className="border-b pb-3">
                <h4 className="font-medium text-slate-800 mb-3">Referees / Guarantors</h4>
                
                {/* Referee 1 */}
                <div className="bg-slate-50 p-3 rounded-lg mb-3">
                  <p className="text-sm font-medium text-slate-700 mb-2">Referee/Guarantor 1 *</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Full Name *</Label>
                      <Input 
                        value={formData.referee1_name} 
                        onChange={(e) => setFormData({...formData, referee1_name: e.target.value})} 
                        placeholder="Referee name"
                        required 
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Phone Number *</Label>
                      <Input 
                        value={formData.referee1_phone} 
                        onChange={(e) => setFormData({...formData, referee1_phone: e.target.value})} 
                        placeholder="Referee phone"
                        required 
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <Label className="text-xs">Address *</Label>
                    <Input 
                      value={formData.referee1_address} 
                      onChange={(e) => setFormData({...formData, referee1_address: e.target.value})} 
                      placeholder="Referee address"
                      required 
                    />
                  </div>
                </div>

                {/* Referee 2 */}
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-2">Referee/Guarantor 2 *</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Full Name *</Label>
                      <Input 
                        value={formData.referee2_name} 
                        onChange={(e) => setFormData({...formData, referee2_name: e.target.value})} 
                        placeholder="Referee name"
                        required 
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Phone Number *</Label>
                      <Input 
                        value={formData.referee2_phone} 
                        onChange={(e) => setFormData({...formData, referee2_phone: e.target.value})} 
                        placeholder="Referee phone"
                        required 
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <Label className="text-xs">Address *</Label>
                    <Input 
                      value={formData.referee2_address} 
                      onChange={(e) => setFormData({...formData, referee2_address: e.target.value})} 
                      placeholder="Referee address"
                      required 
                    />
                  </div>
                </div>
              </div>

              {/* Driving Experience & Position */}
              <div>
                <h4 className="font-medium text-slate-800 mb-3">Driving & Position</h4>
                <div>
                  <Label>Driving Experience *</Label>
                  <Textarea 
                    value={formData.driving_experience} 
                    onChange={(e) => setFormData({...formData, driving_experience: e.target.value})} 
                    placeholder="Describe your driving experience (years of experience, types of vehicles driven, etc.). Write 'None' if you have no driving experience."
                    required 
                  />
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="has_license"
                    checked={formData.has_driving_license}
                    onChange={(e) => setFormData({...formData, has_driving_license: e.target.checked})}
                    className="w-4 h-4 rounded border-slate-300"
                  />
                  <Label htmlFor="has_license" className="cursor-pointer">I have a valid driving license</Label>
                </div>
                <div className="mt-3">
                  <Label>Position Applying For *</Label>
                  <Select 
                    value={formData.position_applied} 
                    onValueChange={(v) => setFormData({...formData, position_applied: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent>
                      {POSITIONS.map(pos => (
                        <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">
                Submit Application
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Submissions Table - Only for Admin/Manager */}
      {canViewSubmissions ? (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-[900px]">
              <thead>
                <tr>
                  <th>Applicant Name</th>
                  <th>Position</th>
                  <th>Telephone</th>
                  <th>Application Date</th>
                  <th>Interview</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {forms.map(form => (
                  <tr key={form.id}>
                    <td className="font-medium">{form.first_name} {form.surname}</td>
                    <td>
                      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                        {form.position_applied}
                      </span>
                    </td>
                    <td>{form.telephone}</td>
                    <td>{formatDate(form.application_date)}</td>
                    <td>
                      {form.interview_scheduled ? (
                        <div className="text-xs">
                          <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                            {formatDate(form.interview_date)} {form.interview_time}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-xs">Not scheduled</span>
                      )}
                    </td>
                    <td>{getStatusBadge(form.status)}</td>
                    <td>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => onView(form)} title="View Details">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => openInterviewDialog(form)}
                          title="Schedule Interview"
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        >
                          <Calendar className="w-4 h-4" />
                        </Button>
                        {isAdmin && form.email && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => openResubmissionDialog(form)}
                            title="Send Resubmission Link"
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {forms.length === 0 && <div className="text-center py-8 text-slate-500">No applications found</div>}
          </div>
        </div>
      ) : (
        <div className="card p-6 text-center">
          <div className="text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">Job applications submitted will be reviewed by Admin/Manager</p>
            <p className="text-sm mt-1">Thank you for your interest in Wilyrash Farm</p>
          </div>
        </div>
      )}

      {/* Interview Scheduling Dialog */}
      <Dialog open={interviewDialogOpen} onOpenChange={setInterviewDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Schedule Interview
            </DialogTitle>
          </DialogHeader>
          {selectedApplication && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{selectedApplication.first_name} {selectedApplication.surname}</p>
                <p className="text-sm text-slate-500">Position: {selectedApplication.position_applied}</p>
                <p className="text-sm text-slate-500">Tel: {selectedApplication.telephone}</p>
                {selectedApplication.email && (
                  <p className="text-sm text-slate-500">Email: {selectedApplication.email}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Interview Date *</Label>
                  <Input 
                    type="date"
                    value={interviewData.interview_date}
                    onChange={(e) => setInterviewData({...interviewData, interview_date: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <Label>Interview Time *</Label>
                  <Input 
                    type="time"
                    value={interviewData.interview_time}
                    onChange={(e) => setInterviewData({...interviewData, interview_time: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Interview Location *</Label>
                <Input 
                  value={interviewData.interview_location}
                  onChange={(e) => setInterviewData({...interviewData, interview_location: e.target.value})}
                  placeholder="e.g., Wilyrash Farm Office, Main Building"
                />
              </div>
              
              <div>
                <Label>Additional Notes (Optional)</Label>
                <Textarea 
                  value={interviewData.interview_notes}
                  onChange={(e) => setInterviewData({...interviewData, interview_notes: e.target.value})}
                  placeholder="Any special instructions for the applicant..."
                  rows={3}
                />
              </div>

              {selectedApplication.email && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  An email notification will be sent to the applicant with interview details.
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setInterviewDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={handleScheduleInterview}
                >
                  {selectedApplication.interview_scheduled ? 'Update Interview' : 'Schedule Interview'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Resubmission Link Dialog */}
      <Dialog open={resubmissionDialogOpen} onOpenChange={setResubmissionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-600" />
              Send Resubmission Link
            </DialogTitle>
          </DialogHeader>
          {selectedApplication && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  This will send a one-time link to the applicant allowing them to submit a new application. 
                  The link will be valid for <strong>7 days</strong>.
                </p>
              </div>
              
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{selectedApplication.first_name} {selectedApplication.surname}</p>
                <p className="text-sm text-slate-500">Position: {selectedApplication.position_applied}</p>
                <p className="text-sm text-slate-500">Current Status: {selectedApplication.status}</p>
              </div>
              
              <div>
                <Label>Applicant Email</Label>
                <Input 
                  type="email"
                  value={resubmissionEmail}
                  onChange={(e) => setResubmissionEmail(e.target.value)}
                  placeholder="applicant@email.com"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setResubmissionDialogOpen(false);
                    setResubmissionEmail('');
                    setSelectedApplication(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={handleSendResubmissionLink}
                  disabled={sendingResubmission || !resubmissionEmail}
                >
                  {sendingResubmission ? 'Sending...' : 'Send Resubmission Link'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Staff Resignation Form Component
const StaffResignationForm = ({ forms, loading, isAdmin, canViewSubmissions, onRefresh, onView, getStatusBadge, API, getAuthHeader, currentUser }) => {
  const [open, setOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [formData, setFormData] = useState({
    resignation_date: '',
    last_working_date: '',
    reason_for_leaving: '',
    notice_period_served: true,
    outstanding_loans: false,
    loan_amount: '',
    company_property_returned: false,
    property_details: '',
    forwarding_address: '',
    forwarding_phone: '',
    feedback: '',
    willing_to_return: false,
    exit_interview_requested: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        attachments: attachments.map(a => a.id)
      };
      await axios.post(`${API}/forms/staff-resignation`, submitData, getAuthHeader());
      toast.success('Resignation form submitted successfully');
      setOpen(false);
      setFormData({
        resignation_date: '',
        last_working_date: '',
        reason_for_leaving: '',
        notice_period_served: true,
        outstanding_loans: false,
        loan_amount: '',
        company_property_returned: false,
        property_details: '',
        forwarding_address: '',
        forwarding_phone: '',
        feedback: '',
        willing_to_return: false,
        exit_interview_requested: false
      });
      setAttachments([]);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit resignation form');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Staff Resignation Forms</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-600 hover:bg-slate-700">
              <Plus className="w-4 h-4 mr-2" /> Submit Resignation
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Staff Resignation Form</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Submitting User Info */}
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="text-sm text-slate-600">Employee:</p>
                <p className="font-semibold text-slate-900">{currentUser?.name || 'Unknown User'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Resignation Date *</Label>
                  <Input 
                    type="date" 
                    value={formData.resignation_date} 
                    onChange={(e) => setFormData({...formData, resignation_date: e.target.value})} 
                    required 
                  />
                </div>
                <div>
                  <Label>Last Working Date *</Label>
                  <Input 
                    type="date" 
                    value={formData.last_working_date} 
                    onChange={(e) => setFormData({...formData, last_working_date: e.target.value})} 
                    required 
                  />
                </div>
              </div>

              <div>
                <Label>Reason for Leaving *</Label>
                <Select 
                  value={formData.reason_for_leaving} 
                  onValueChange={(v) => setFormData({...formData, reason_for_leaving: v})}
                >
                  <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal_reasons">Personal Reasons</SelectItem>
                    <SelectItem value="better_opportunity">Better Job Opportunity</SelectItem>
                    <SelectItem value="relocation">Relocation</SelectItem>
                    <SelectItem value="health_issues">Health Issues</SelectItem>
                    <SelectItem value="family_responsibilities">Family Responsibilities</SelectItem>
                    <SelectItem value="career_change">Career Change</SelectItem>
                    <SelectItem value="further_education">Further Education</SelectItem>
                    <SelectItem value="retirement">Retirement</SelectItem>
                    <SelectItem value="dissatisfaction">Job Dissatisfaction</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-medium text-amber-800">Notice Period & Obligations</h4>
                
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="notice_period" 
                    checked={formData.notice_period_served} 
                    onChange={(e) => setFormData({...formData, notice_period_served: e.target.checked})} 
                  />
                  <Label htmlFor="notice_period">I confirm I will serve the required notice period</Label>
                </div>

                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="outstanding_loans" 
                    checked={formData.outstanding_loans} 
                    onChange={(e) => setFormData({...formData, outstanding_loans: e.target.checked})} 
                  />
                  <Label htmlFor="outstanding_loans">I have outstanding loans with the company</Label>
                </div>

                {formData.outstanding_loans && (
                  <div>
                    <Label>Outstanding Loan Amount</Label>
                    <Input 
                      type="number" 
                      value={formData.loan_amount} 
                      onChange={(e) => setFormData({...formData, loan_amount: e.target.value})} 
                      placeholder="Enter loan amount"
                    />
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="property_returned" 
                    checked={formData.company_property_returned} 
                    onChange={(e) => setFormData({...formData, company_property_returned: e.target.checked})} 
                  />
                  <Label htmlFor="property_returned">I have returned all company property</Label>
                </div>

                {!formData.company_property_returned && (
                  <div>
                    <Label>Property Details (items to be returned)</Label>
                    <Textarea 
                      value={formData.property_details} 
                      onChange={(e) => setFormData({...formData, property_details: e.target.value})} 
                      placeholder="List any company property you still have"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-3 p-3 bg-slate-50 rounded-lg border">
                <h4 className="font-medium text-slate-800">Contact Information</h4>
                <div>
                  <Label>Forwarding Address</Label>
                  <Textarea 
                    value={formData.forwarding_address} 
                    onChange={(e) => setFormData({...formData, forwarding_address: e.target.value})} 
                    placeholder="Address where documents can be sent"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Forwarding Phone</Label>
                  <Input 
                    value={formData.forwarding_phone} 
                    onChange={(e) => setFormData({...formData, forwarding_phone: e.target.value})} 
                    placeholder="Phone number for future contact"
                  />
                </div>
              </div>

              <div>
                <Label>Feedback / Comments</Label>
                <Textarea 
                  value={formData.feedback} 
                  onChange={(e) => setFormData({...formData, feedback: e.target.value})} 
                  placeholder="Any feedback about your experience working here (optional)"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="willing_return" 
                    checked={formData.willing_to_return} 
                    onChange={(e) => setFormData({...formData, willing_to_return: e.target.checked})} 
                  />
                  <Label htmlFor="willing_return">I would consider returning to work here in the future</Label>
                </div>

                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="exit_interview" 
                    checked={formData.exit_interview_requested} 
                    onChange={(e) => setFormData({...formData, exit_interview_requested: e.target.checked})} 
                  />
                  <Label htmlFor="exit_interview">I request an exit interview</Label>
                </div>
              </div>

              <FileAttachment attachments={attachments} setAttachments={setAttachments} />
              
              <Button type="submit" className="w-full bg-slate-600 hover:bg-slate-700">
                Submit Resignation Form
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Submissions Table - Only for Admin/Manager */}
      {canViewSubmissions ? (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-[800px]">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Resignation Date</th>
                  <th>Last Working Day</th>
                  <th>Reason</th>
                  <th>Notice Period</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {forms.map(form => (
                  <tr key={form.id}>
                    <td className="font-medium">{form.employee_name}</td>
                    <td>{formatDate(form.resignation_date)}</td>
                    <td>{formatDate(form.last_working_date)}</td>
                    <td>
                      <span className="text-sm">
                        {form.reason_for_leaving?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </td>
                    <td>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        form.notice_period_served ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {form.notice_period_served ? 'Will Serve' : 'Not Serving'}
                      </span>
                    </td>
                    <td>{getStatusBadge(form.status)}</td>
                    <td>
                      <Button size="sm" variant="ghost" onClick={() => onView(form)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {forms.length === 0 && <div className="text-center py-8 text-slate-500">No resignation forms found</div>}
          </div>
        </div>
      ) : (
        <div className="card p-6 text-center">
          <div className="text-slate-500">
            <UserMinus className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">Form submitted successfully will be reviewed by Admin/Manager</p>
            <p className="text-sm mt-1">Contact Admin or Manager to view or edit your submissions</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Forms;
